import { createRouter, createWebHashHistory } from 'vue-router';
import { defineAsyncComponent, markRaw } from 'vue';
import { TTSShell } from '@/global/TTS';
import TTWindowManager from '@/components/TTWindowManager.vue';
import TTVueUtils from '@/services/TTVueUtils';
import { useGlobalStore } from '@/components/common/reactive_store/useGlobalStore';
import { RouteSurfacePolicy } from '@/components/RouteSurfacePolicy';
import { useTTViewStackStore } from '@/stores/TTViewStack';
import { TTRouteData } from '@/components/views/base/ViewData';
import { TTViewLoaderAsync } from '@/components/views/base/TTViewLoaderAsync';
import { TTInteractionManagerObj } from '@/services/TTInteractionManager';
import {
	APP_HISTORY_DEPTH_STATE_KEY,
	buildAppHistoryState,
	getAppHistoryDepth,
	getNavigationRequestKey,
	getNextTimestampParam,
} from '@/components/TTRouterState';

/**
 * Central router singleton plus route/path and presentation-launch helpers for the main UI.
 * Direct custom shells and report modals retain their transient lifecycle without
 * writing URL/history or persisted view-stack state.
 *
 * Router/view-stack contract:
 * - navigateRecord() applies the saved record-opening preference only at explicit
 *   CRUD launch sites. navigate()/navigatePath() also serve Jump To, history,
 *   nested dialogs and utility screens, so they must not infer that preference.
 *   Issue #3924: It also retains the launcher's stack identity for modal returns;
 *   optional live parent/refresh references keep their separate ownership.
 *
 * Issue #3924: Record navigation has three related, but independent, owners:
 * 1. The actual launcher determines presentation eligibility (modal/right pane).
 * 2. The return parent is the mounted window Save/Cancel must resurface. A live
 *    parent reference takes priority over the ID-only source fallback.
 * 3. The refresh parent owns changed rows; it can be an embedded list or another
 *    mounted same-module list without being the window the user should return to.
 * A module name, current active window, or refresh target cannot stand in for all
 * three. In particular, Dashboard owns a real stack ID but no module TTRouteData.
 *
 * Reproduction / acceptance checks for that distinction:
 * - Open TimeSheet, then Dashboard -> Accrual Balance Custom List -> double-click
 *   a row -> Cancel. Return to that mounted Dashboard, preserving its selection;
 *   the previous-view fallback alone can choose TimeSheet instead.
 * - Keep an Employees list mounted, then launch an Employee Custom List record
 *   from Dashboard -> change a field -> Save. The list may receive changed rows,
 *   but the user must return to Dashboard rather than that separate list.
 * - Leave unsaved changes in a parent Employee edit -> open a Contacts child ->
 *   Cancel. Resurface the same dirty parent DOM; rebuilding its route loses edits.
 * - Enable Open Records in Right Pane -> open/save successive records from the
 *   same source. Placement, slot replacement and discard confirmation must retain
 *   the real source even while a different editor is the active window.
 *
 * This is a live-window return contract, not a promise to recreate missing hosts.
 * TTViewStack persistence currently skips Dashboard because it has no module route.
 * Reloading a Dashboard-launched record can therefore leave no Dashboard to return
 * to; see TTViewStackPersistentState.buildRouteState()/buildViewState(). Do not hide
 * that separate persistence limitation by manufacturing a Dashboard module route
 * or by serializing a Vue component as route data.
 *
 * Remaining router/view-stack contracts:
 * - openInRightPane() is an explicit placement primitive, not a permission or
 *   dirty-state gate. Automatic record callers must go through navigateRecord()
 *   before placement can normalize ancestors or mutate an occupied detail slot.
 * - A route change should not automatically mean "create a new DOM/window".
 *   The router decides whether a navigation should create a stack item, resurface
 *   an existing stack item, or update the route data for an existing stack item.
 * - Issue #3719: A child edit can have the same module/action as its parent.
 *   Same-surface reuse is valid only for the same record; a different linked
 *   record needs its own stack entry so the parent editor keeps unsaved state.
 * - Normal list views should keep their mounted DOM when users open edit/new
 *   records. On save, the parent list receives refresh_ids so the table can fetch
 *   and inject just the changed/new record, then highlight it. This preserves
 *   scroll position, filters, selected layout, and other local UI state. It also
 *   lets a newly-created record appear even when it would not match the current
 *   search filters.
 * - Normal cancel paths only resurface the parent list DOM. They should not
 *   search, refresh, or inject row data because no committed data changed.
 * - Special views such as TimeSheet and Schedule are different. A punch, absence,
 *   or shift save can affect totals, neighboring rows, date ranges, and other
 *   derived state, so their parent refresh hook deliberately runs the special
 *   view's own search/reload path. Cancel still only resurfaces existing DOM.
 * - URL-only state changes, such as TimeSheet date/employee/gear state or
 *   Schedule mode, can create browser history without creating more stack rows.
 *   RouteSurfacePolicy decides which params are URL-only state and which params
 *   are structural enough to require a route_revision remount.
 * - URL-only replacements must also update Vue Router's history location. A
 *   later push otherwise overwrites the visible source entry with a stale route,
 *   making restored windows require extra browser Back steps.
 * - Explicit TTRouteData navigation carries initialization payload outside the
 *   URL, so resurfacing an existing destination must remount it to consume that
 *   payload. This is how schema-owned Jump To employee/date/filter defaults win
 *   over state retained by a previously mounted list.
 *   Issue #4005: Explicit New payloads also cross the identical-URL guard so
 *   Save & New initializes a fresh form; URL-only identical routes remain no-ops.
 *   Save & New supplies its original return ownership when building that fresh route.
 * - Some special list views opt into router-owned "return state". When a user
 *   leaves one of those views, the router captures a small state snapshot. The
 *   snapshot is restored only for generic left-menu returns, so Jump To,
 *   bookmarks, deep links, and explicit route params/data remain exact.
 * - Issue #3730: Vue login query credentials feed TTLogin's existing defaults
 *   so shared links can prefill without advancing or submitting the login flow.
 */
class TTRouter {
	// Shell admission must match ViewData, ContextMenuManager and TTViewStack.
	// Only structural shell_name travels in the URL; task data/callbacks stay in the stack.
	static modal_route_actions = new Set( ['edit', 'new', 'view', 'shell', 'wizard'] );
	static url_auto_fill_reserved_parameters = new Set( [
		'm', 'a', 'id', 'f', 't', 'tab', 'sid', 'sm',
		'edit_name', 'audit_table_name', 'list_name', 'shell_name', 'wizard_name',
		'__proto__', 'prototype', 'constructor',
	] );
	static last_timestamp_param = 0;
	static navigation_requests = new Map();
	static pending_navigation_promises = new Set();
	static report_modal_handler = null;
	static return_state_by_route = {};
	static router = null;
	static async_components = {
		TTDashboardRoute: null,
	};

	/**
	 * Return the route table used by the main UI hash router.
	 */
	static getRoutes() {
		return [
			// Install is public and owns its fields, so it must bypass both the
			// authenticated application shell and TTViewLoader/TTS ownership.
			{
				path: '/install',
				name: 'install',
				component: () => import( '@/components/wizard/InstallTTWizard.vue' ),
				props: route => ( {
					initial_step: typeof route.query.step === 'string' ? route.query.step : 'license',
					external_installer_mode: Number( route.query.external_installer ) === 1 ? 1 : 0,
					public_mode: true,
				} ),
				meta: { stack: false }
			},
			{
				path: '/',
				name: 'authenticated',
				component: () => import( '@/components/TTAuthenticatedUI' ),
				children: [
					{
						path: ':pathMatch(.*)*',
						name: 'catch-all',
						component: () => import( '@/components/TTDashboardRoute' ),
						props: true,
						meta: { stack: true }
					}
				]
			},
			{
				path: '/login',
				name: 'login',
				component: () => import( '@/components/login/TTLogin' ),
				// Issue #3730: Map Vue login query values into TTLogin's defaults without advancing or submitting the login flow.
				props: route => ( {
					default_user_name: typeof route.query.user_name === 'string' ? route.query.user_name : null,
					default_user_password: typeof route.query.password === 'string' ? route.query.password : null,
				} ),
				meta: { stack: true }
			}
		];
	}

	/**
	 * Create and wire the shared Vue router once.
	 */
	static createVueRouter() {
		if ( this.router ) {
			return this.router;
		}

		this.router = createRouter( {
			history: createWebHashHistory(),
			routes: this.getRoutes(),
		} );
		this.initializeAppHistoryState();

		this.router.beforeEach( async ( to, from ) => {
			return await this.processRoute( to, from );
		} );

		this.router.afterEach( ( to, from, failure ) => {
			this.afterRoute( to, from, failure );
		} );

		return this.router;
	}

	/**
	 * Return the shared Vue router instance.
	 */
	static getRouter() {
		if ( this.router ) {
			return this.router;
		}

		Debug.Text( 'Unable to resolve router dependency.', 'TTRouter.js', 'TTRouter', 'getRouter', 10 );
		return null;
	}

	/**
	 * Return the live TTViewStack store.
	 */
	static getViewStackStore() {
		return useTTViewStackStore();
	}

	/**
	 * Return the explicit stack target id for a URL-only update.
	 */
	static getUrlUpdateTargetViewId( options = {} ) {
		return options?.view_stack_id
			?? options?.view_id
			?? options?.tt_route_data?.getViewStackId?.()
			?? options?.tt_route_data?.getInstanceId?.()
			?? null;
	}

	/**
	 * Resolve which stack view owns a URL-only update.
	 *
	 * Most callers update the active route, so the active view is the default.
	 * Special views can finish async work after the user has navigated elsewhere;
	 * those callers pass their route data so stale async completions update the
	 * hidden stack entry instead of leaking TimeSheet/Schedule params into the
	 * currently active module URL.
	 */
	static getUrlUpdateTargetView( TTViewStackStore, options = {} ) {
		if ( !TTViewStackStore ) {
			return null;
		}

		const target_view_id = this.getUrlUpdateTargetViewId( options );

		if ( target_view_id ) {
			return TTViewStackStore.getViewById?.( target_view_id ) ?? null;
		}

		return TTViewStackStore.getViewById?.( TTViewStackStore.active_view_id ) ?? null;
	}

	/**
	 * Update an inactive stack view's remembered URL without navigating.
	 *
	 * This is intentionally different from browser history. If a mounted special
	 * view completes a stale async request after the user has moved to another
	 * module, we still let that view remember its own latest path, but we must not
	 * push/replace the visible browser URL or touch the active stack item.
	 */
	static updateInactiveStackViewURL( view, path ) {
		if ( !view || typeof path !== 'string' || path.length === 0 ) {
			return '';
		}

		const next_path = this.addTimestampParam( this.getUrlParameters( path ).toString() );
		const query_params = this.getUrlParameters( next_path );
		const module = query_params.get( 'm' );
		const action = query_params.get( 'a' ) || 'list';
		const record_id = query_params.get( 'id' ) || null;
		const flags = query_params.get( 'f' ) || null;
		const raw_tab = query_params.get( 'tab' );
		const tab = raw_tab !== null && raw_tab !== '' && Number.isFinite( Number( raw_tab ) )
			? Number( raw_tab )
			: null;

		view.path = '/' + next_path;

		if ( module && view.tt_route_data ) {
			this.updateRouteDataFromUrlParameters( view.tt_route_data, query_params, module, action, record_id, flags, tab, true );
		}

		return next_path;
	}

	/**
	 * Return the live global store.
	 */
	static getGlobalStore() {
		return useGlobalStore();
	}

	/**
	 * Clear in-memory special-view return state for logout/session reset.
	 * Issue #3858: A denied view can discard just its own menu snapshot after
	 * navigation captures it; callers without a module still reset the session.
	 */
	static clearReturnState( module = null, action = 'list' ) {
		const key = this.getReturnStateKey( module, action );
		if ( key ) {
			delete this.return_state_by_route[key];
			return;
		}

		this.return_state_by_route = {};
	}

	/**
	 * Build the stable map key used for a supported return-state route.
	 */
	static getReturnStateKey( module, action = 'list' ) {
		if ( !module ) {
			return null;
		}

		return module + ':' + ( action || 'list' );
	}

	/**
	 * Normalize a view-owned return-state snapshot before storing or restoring it.
	 */
	static normalizeReturnStateSnapshot( snapshot = {} ) {
		const copyPayload = ( source = {} ) => {
			if ( !source || typeof source !== 'object' || Array.isArray( source ) ) {
				return {};
			}

			return Object.entries( source ).reduce( ( copy, [key, value] ) => {
				if ( value === null || typeof value === 'undefined' || value === '' ) {
					return copy;
				}

				copy[key] = typeof value === 'object'
					? JSON.parse( JSON.stringify( value ) )
					: value;
				return copy;
			}, {} );
		};
		const normalized = {
			params: copyPayload( snapshot?.params ),
			data: copyPayload( snapshot?.data ),
		};

		return Object.keys( normalized.params ).length > 0 || Object.keys( normalized.data ).length > 0 ? normalized : null;
	}

	/**
	 * Ask a mounted stack view for its opt-in return-state snapshot.
	 *
	 * Router owns capture/restore timing, while views own the exact params/data
	 * they allow to be remembered. That keeps TimeSheet/Schedule-specific keys out
	 * of the router and avoids scattered per-field cache writes.
	 */
	static getReturnStateSnapshotFromView( view ) {
		const view_reference = view?.tt_route_data?.getViewReference?.();
		const component_snapshot = typeof view_reference?.getReturnState === 'function'
			? view_reference.getReturnState()
			: null;
		return this.normalizeReturnStateSnapshot( component_snapshot );
	}

	/**
	 * Capture a mounted view's return state when that view explicitly opts in.
	 */
	static captureReturnStateFromView( view ) {
		if ( !view?.tt_route_data ) {
			return null;
		}

		const module = view.tt_route_data.getModule?.();
		const action = view.tt_route_data.getAction?.() || 'list';
		const key = this.getReturnStateKey( module, action );

		if ( !key ) {
			return null;
		}

		const snapshot = this.getReturnStateSnapshotFromView( view );
		if ( !snapshot ) {
			return null;
		}

		this.return_state_by_route[key] = snapshot;

		return this.return_state_by_route[key];
	}

	/**
	 * Capture the source view only when navigation is leaving that routed surface.
	 */
	static captureReturnStateWhenLeavingSurface( TTViewStackStore, source_view, target_module, target_action ) {
		if ( !source_view || !target_module ) {
			return null;
		}

		if ( this.isSameRouteSurface( TTViewStackStore, source_view, target_module, target_action || 'list' ) ) {
			return null;
		}

		return this.captureReturnStateFromView( source_view );
	}

	/**
	 * Allow return-state restore only for generic left-menu navigation.
	 */
	static canRestoreReturnStateForNavigation( module, tt_route_data = null, path = '', options = {} ) {
		return !!module
			&& options?.navigation_source === 'left_menu'
			&& !this.hasExplicitRouteDataForReturnState( tt_route_data )
			&& !this.hasExplicitPathParamsForReturnState( path );
	}

	/**
	 * Treat any caller-supplied route data as targeted.
	 */
	static hasExplicitRouteDataForReturnState( tt_route_data = null ) {
		const route_data = tt_route_data?.getData?.() || {};

		return Object.keys( route_data ).length > 0;
	}

	/**
	 * Treat any URL params beyond route identity/timestamp as targeted.
	 */
	static hasExplicitPathParamsForReturnState( path ) {
		const params = this.getUrlParameters( path || '' );
		const allowed_keys = new Set( ['m', 'a', 't'] );

		for ( const key of params.keys() ) {
			if ( !allowed_keys.has( key ) ) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Find an existing mounted stack entry for an opt-in return-state route.
	 */
	static getMountedReturnStateView( module, action = 'list' ) {
		const TTViewStackStore = this.getViewStackStore();

		if ( !TTViewStackStore ) {
			return null;
		}

		const matchesMountedReturnStateView = ( view ) => {
			return !!view
				&& this.isSameRouteSurface( TTViewStackStore, view, module, action || 'list' )
				&& this.getReturnStateSnapshotFromView( view ) !== null;
		};
		const active_view = TTViewStackStore.getViewById?.( TTViewStackStore.active_view_id );

		if ( matchesMountedReturnStateView( active_view ) ) {
			return active_view;
		}

		for ( let i = TTViewStackStore.view_stack.length - 1; i >= 0; i-- ) {
			const candidate = TTViewStackStore.view_stack[i];
			if ( matchesMountedReturnStateView( candidate ) ) {
				return candidate;
			}
		}

		return null;
	}

	/**
	 * Return mounted DOM only for generic left-menu returns to supported views.
	 */
	static getMountedReturnStateViewForNavigation( module, action, tt_route_data = null, path = '', options = {} ) {
		if ( !this.canRestoreReturnStateForNavigation( module, tt_route_data, path, options ) ) {
			return null;
		}

		return this.getMountedReturnStateView( module, action || 'list' );
	}

	/**
	 * Return the mounted view's route path so DOM state wins over remembered state.
	 */
	static getMountedReturnStateViewPath( view, fallback_path = '' ) {
		const mounted_path = typeof view?.path === 'string' ? this.stripTimestampParam( view.path ) : '';

		return mounted_path || fallback_path;
	}

	/**
	 * Return saved state only for generic left-menu navigations.
	 */
	static getReturnStateForNavigation( module, action, tt_route_data = null, path = '', options = {} ) {
		if ( !this.canRestoreReturnStateForNavigation( module, tt_route_data, path, options ) ) {
			return null;
		}

		const key = this.getReturnStateKey( module, action || 'list' );
		const stored_state = key ? this.return_state_by_route?.[key] : null;
		if ( !stored_state ) {
			return null;
		}

		return this.normalizeReturnStateSnapshot( stored_state );
	}

	/**
	 * Merge saved data payload into route data for restored left-menu navigation.
	 */
	static applyReturnStateDataToRouteData( tt_route_data, return_state = null ) {
		if ( !tt_route_data || !return_state || Object.keys( return_state.data || {} ).length === 0 ) {
			return tt_route_data;
		}

		tt_route_data.setData( {
			...( tt_route_data.getData?.() || {} ),
			...return_state.data,
		} );

		return tt_route_data;
	}

	/**
	 * Merge saved URL params into the route path for restored left-menu navigation.
	 */
	static applyReturnStateParamsToPath( path, return_state = null ) {
		if ( typeof path !== 'string' || path.length === 0 || !return_state || Object.keys( return_state.params || {} ).length === 0 ) {
			return path;
		}

		const params = this.getUrlParameters( path );
		Object.entries( return_state.params ).forEach( ( [key, value] ) => {
			if ( value !== null && typeof value !== 'undefined' && value !== '' ) {
				params.set( key, String( value ) );
			}
		} );

		return params.toString();
	}

	/**
	 * Apply saved state to a TTRouteData navigation before the hash is pushed.
	 */
	static applyReturnStateToRouteNavigation( tt_route_data, path, options = {} ) {
		const return_state = this.getReturnStateForNavigation(
			tt_route_data?.getModule?.(),
			tt_route_data?.getAction?.(),
			tt_route_data,
			path,
			options
		);

		if ( !return_state ) {
			return path;
		}

		this.applyReturnStateDataToRouteData( tt_route_data, return_state );
		return this.applyReturnStateParamsToPath( path, return_state );
	}

	/**
	 * Apply saved URL params to a raw path navigation when it came from the left menu.
	 */
	static applyReturnStateToPathNavigation( path, options = {} ) {
		const params = this.getUrlParameters( path );
		const module = params.get( 'm' );
		const action = params.get( 'a' ) || 'list';
		const return_state = this.getReturnStateForNavigation( module, action, null, path, options );

		return return_state ? this.applyReturnStateParamsToPath( path, return_state ) : path;
	}

	/**
	 * Create a TTRouteData instance for URL-driven routes.
	 */
	static createRouteData() {
		return new TTRouteData();
	}

	/**
	 * Return the async default dashboard route without eagerly importing route components.
	 */
	static getTTDashboardRouteComponent() {
		if ( !this.async_components.TTDashboardRoute ) {
			this.async_components.TTDashboardRoute = defineAsyncComponent( () => import( '@/components/TTDashboardRoute' ) );
		}

		return this.async_components.TTDashboardRoute;
	}

	/**
	 * Return the shared lazy generic-view component without reintroducing the
	 * anonymous-startup global registration. This public router method remains
	 * the compatibility boundary for existing route construction callsites.
	 */
	static getTTViewLoaderComponent() {
		return TTViewLoaderAsync;
	}

	/**
	 * Normalize a route-like path for stack comparisons.
	 */
	static normalizePath( path ) {
		const params = this.getUrlParameters( path );
		params.delete( 't' );

		return params.toString();
	}

	/**
	 * Identify route actions that render as modal overlays.
	 */
	static isModalRouteAction( route_action ) {
		return this.modal_route_actions.has( route_action );
	}

	/**
	 * Resolve modal surface mode from explicit view-stack metadata before falling back to action defaults.
	 */
	static isModalRouteSurface( tt_route_data, target_action ) {
		const view_stack = tt_route_data?.getData?.()?.view_stack;
		if ( view_stack && typeof view_stack === 'object' && Array.isArray( view_stack ) !== true ) {
			if ( view_stack.is_side_panel === true ) {
				return false;
			}

			if ( typeof view_stack.is_modal_view === 'boolean' ) {
				return view_stack.is_modal_view === true;
			}
		}

		const route_action = tt_route_data?.getAction?.() ?? target_action;
		return this.isModalRouteAction( route_action );
	}

	/**
	 * Identify primary stack views that can anchor subsequent routing.
	 */
	static isPrimaryRoutingAnchorView( TTViewStackStore, view ) {
		return !!view
			&& TTViewStackStore.isSidePanelView( view ) !== true
			&& TTViewStackStore.isModalView( view ) !== true;
	}

	/**
	 * Find the nearest primary stack view, preferring visible views.
	 */
	static findNearestPrimaryRoutingAnchorIndex( TTViewStackStore, source_index ) {
		const search_ranges = [
			{ start: source_index + 1, end: TTViewStackStore.view_stack.length, step: 1 },
			{ start: source_index - 1, end: -1, step: -1 },
		];

		for ( const require_visible of [true, false] ) {
			for ( const range of search_ranges ) {
				for ( let i = range.start; i !== range.end; i += range.step ) {
					const candidate = TTViewStackStore.view_stack[i];

					if ( !this.isPrimaryRoutingAnchorView( TTViewStackStore, candidate ) ) {
						continue;
					}

					if ( require_visible && candidate.visible !== true ) {
						continue;
					}

					return i;
				}
			}
		}

		return -1;
	}

	/**
	 * Resolve the source view used to place or overlay the next routed view.
	 */
	static getNormalizedRoutingSource( TTViewStackStore, preferred_source_view_id = null ) {
		if ( !TTViewStackStore ) {
			return {
				source_index: -1,
				source_view: null,
			};
		}

		// Semantic companion navigation can target a visible source that is not the
		// current active primary. Prefer that explicit owner so route insertion and
		// reuse stay attached to the same source/slot relationship.
		let source_index = preferred_source_view_id ? TTViewStackStore.view_stack.findIndex( v => v.id === preferred_source_view_id ) : -1;
		if ( source_index < 0 ) {
			source_index = TTViewStackStore.active_view_id ? TTViewStackStore.view_stack.findIndex( v => v.id === TTViewStackStore.active_view_id ) : -1;
		}
		let source_view = source_index >= 0 ? TTViewStackStore.view_stack[source_index] : null;
		const source_is_modal = source_view && TTViewStackStore.isModalView( source_view ) === true;

		if ( source_is_modal ) {
			source_view.visible = false;
			source_index = this.findNearestPrimaryRoutingAnchorIndex( TTViewStackStore, source_index );
			source_view = source_index >= 0 ? TTViewStackStore.view_stack[source_index] : null;
		}

		return {
			source_index: source_index,
			source_view: source_view,
		};
	}

	/**
	 * Apply the shared placement rules for reused stack views.
	 */
	static positionReusedView( TTViewStackStore, reuse_view, reuse_index, source_view, source_index, view_stack_overrides ) {
		if ( source_view && source_view.id !== reuse_view.id ) {
			if ( !view_stack_overrides.keep_source_visible ) {
				this.hideOrCollapseSourceView( TTViewStackStore, source_view, view_stack_overrides );
			}

			reuse_view.window_row = view_stack_overrides.window_row;
			reuse_view.window_column = view_stack_overrides.window_column;
			reuse_view.window_row_span = view_stack_overrides.window_row_span;
			reuse_view.window_col_span = view_stack_overrides.window_col_span;

			if ( source_index >= 0 && reuse_index >= 0 && reuse_index !== source_index ) {
				const [moved] = TTViewStackStore.view_stack.splice( reuse_index, 1 );
				let insert_at = source_index;

				if ( reuse_index < insert_at ) {
					insert_at--;
				}

				if ( insert_at < 0 ) {
					insert_at = 0;
				}

				TTViewStackStore.view_stack.splice( insert_at, 0, moved );
			}
		}
	}

	static showReusedStackView( TTViewStackStore, view_id, navigation_options = {} ) {
		const show_options = {
			sync_url: false,
		};

		if ( typeof navigation_options?.refresh_on_return === 'boolean' ) {
			show_options.refresh_on_return = navigation_options.refresh_on_return;
		}

		TTViewStackStore.showView( view_id, show_options );
	}

	/**
	 * Preserve the mounted TTRouteData identity while accepting caller-owned
	 * payload for a declared same-surface navigation.
	 */
	static mergeDeclaredSameSurfaceRouteData( mounted_route_data, incoming_route_data ) {
		if ( !mounted_route_data || !incoming_route_data || mounted_route_data === incoming_route_data ) {
			return mounted_route_data;
		}

		const mounted_data = mounted_route_data.getData?.() || {};
		const incoming_data = incoming_route_data.getData?.() || {};
		const mounted_view_stack = mounted_data.view_stack && typeof mounted_data.view_stack === 'object' && Array.isArray( mounted_data.view_stack ) !== true
			? mounted_data.view_stack
			: {};
		const incoming_view_stack = incoming_data.view_stack && typeof incoming_data.view_stack === 'object' && Array.isArray( incoming_data.view_stack ) !== true
			? incoming_data.view_stack
			: {};

		mounted_route_data.setData( {
			...mounted_data,
			...incoming_data,
			view_stack: {
				...mounted_view_stack,
				...incoming_view_stack,
				url_state_params: RouteSurfacePolicy.getDeclaredUrlStateParams( mounted_route_data ),
			},
		} );

		if ( typeof incoming_route_data.getParentView === 'function' ) {
			mounted_route_data.setParentView( incoming_route_data.getParentView() );
		}
		if ( typeof incoming_route_data.getRefreshParentView === 'function' ) {
			mounted_route_data.setRefreshParentView( incoming_route_data.getRefreshParentView() );
		}
		if ( incoming_route_data.getDefaultDestination?.() ) {
			const destination = incoming_route_data.getDefaultDestination();
			mounted_route_data.setDefaultDestination( destination.module, destination.action );
		}

		return mounted_route_data;
	}

	/**
	 * Caller-owned list payload is not URL state. Keep it on the existing rebuild
	 * path so targets such as Employee Jump To -> TimeSheet can initialize from
	 * their user/date payload instead of reusing stale mounted selectors.
	 */
	static hasExplicitSameSurfaceRoutePayload( route_data ) {
		const route_payload = route_data?.getData?.();

		if ( !route_payload || typeof route_payload !== 'object' || Array.isArray( route_payload ) ) {
			return false;
		}

		return Object.keys( route_payload ).some( key => key !== 'view_stack' );
	}

	/**
	 * Jump To navigation to another primary surface should leave the source view
	 * available in the min-tab bar. Normal navigation simply hides the source.
	 */
	static hideOrCollapseSourceView( TTViewStackStore, source_view, view_stack_overrides = {} ) {
		if ( !source_view ) {
			return;
		}

		if ( view_stack_overrides.collapse_source === true && TTViewStackStore?.isSidePanelView?.( source_view ) !== true ) {
			TTViewStackStore.collapseView( source_view.id, true );
			return;
		}

		source_view.visible = false;
	}

	/**
	 * Build placement overrides for the target route relative to the source view.
	 */
	static getViewStackOverrides( route_data, action, source_view, navigation_options = {} ) {
		const view_stack = route_data?.getData?.()?.view_stack ?? {};
		const route_action = ( route_data && typeof route_data.getAction === 'function' )
			? route_data.getAction()
			: action;
		const is_modal_action = this.isModalRouteAction( route_action );
		const is_wizard_action = route_action === 'wizard';
		const has_keep_source_visible = typeof view_stack.keep_source_visible === 'boolean';
		const collapse_source = typeof view_stack.collapse_source === 'boolean'
			? view_stack.collapse_source
			: navigation_options?.collapse_source === true;
		const has_is_side_panel = typeof view_stack.is_side_panel === 'boolean';
		const is_side_panel = has_is_side_panel ? view_stack.is_side_panel : false;
		const has_is_modal_view = typeof view_stack.is_modal_view === 'boolean';
		// Modal-ness is now a window surface mode instead of only an action
		// inference: edit routes default to modal, but a user-popped edit view can
		// stay in the stack, and a list route can be popped out as a modal.
		const is_modal_view = is_side_panel === true ? false : ( has_is_modal_view ? view_stack.is_modal_view : is_modal_action );
		// Issue #3851: Wizards float over the left primary even when launched from an active right pane.
		// Normalize only placement; the explicit route parent still owns return/refresh behavior.
		// This shared override owner serves both fresh routes and reused views, so every modal Wizard gets the same
		// source grid slot without screen-specific CSS or docking it when the Assistant opens. A primary ancestor
		// supplies geometry; it is not a substitute for a nested Wizard/edit route's immediate logical parent.
		// Only use a visible primary: a hidden or broken ancestor must not revive an unrelated background page.
		// Explicit side-panel/docked surface modes bypass this modal-only rule and keep their native placement.
		if ( is_wizard_action && is_modal_view ) {
			const primary_source = this.getViewStackStore()?.getPrimaryAncestorView( source_view );
			if ( primary_source?.visible === true ) source_view = primary_source;
		}
		const source_is_wizard = source_view?.tt_route_data?.getAction?.() === 'wizard';
		let keep_source_visible;
		if ( collapse_source ) {
			keep_source_visible = false;
		} else if ( has_keep_source_visible ) {
			keep_source_visible = view_stack.keep_source_visible;
		} else {
			keep_source_visible = is_modal_view && !!source_view && source_is_wizard !== true;
		}
		const source_row = source_view?.window_row ?? 0;
		const source_column = source_view?.window_column ?? 0;
		const source_row_span = Number.isInteger( source_view?.window_row_span ) && source_view.window_row_span > 0 ? source_view.window_row_span : 1;
		const source_col_span = Number.isInteger( source_view?.window_col_span ) && source_view.window_col_span > 0 ? source_view.window_col_span : 1;
		const has_row = Number.isInteger( view_stack.window_row );
		const has_col = Number.isInteger( view_stack.window_column );
		const default_row = source_row;
		const use_overlay_slot = is_modal_view && !is_side_panel;

		// Modal-style surfaces should stay in the source slot so the previous
		// primary view can remain underneath the centered overlay shell. Explicit
		// non-modal edit routes need the normal adjacent stack placement instead.
		const default_col = keep_source_visible
			? ( use_overlay_slot || is_wizard_action ? source_column : ( source_column + source_col_span ) )
			: source_column;

		return {
			keep_source_visible: keep_source_visible,
			collapse_source: collapse_source,

			window_row: has_row ? view_stack.window_row : default_row,
			window_column: has_col ? view_stack.window_column : default_col,

			window_row_span: view_stack.window_row_span > 0 ? view_stack.window_row_span : source_row_span,
			window_col_span: view_stack.window_col_span > 0 ? view_stack.window_col_span : source_col_span,

			is_side_panel: is_side_panel,
			has_is_side_panel: has_is_side_panel,
			is_modal_view: is_modal_view,
			has_is_modal_view: has_is_modal_view,
		};
	}

	/**
	 * Extract legacy-compatible form values from an editable route.
	 *
	 * This payload is attacker-controlled transport only. EditRecordsTabbed owns
	 * the selected-TTS/UI-visible whitelist before any value can reach a factory.
	 */
	static getURLAutoFillDataFromUrlParameters( query_params ) {
		if ( !( query_params instanceof URLSearchParams ) ) {
			return {};
		}

		const auto_fill_data = new Map();

		query_params.forEach( ( value, raw_key ) => {
			if ( typeof raw_key !== 'string' ) {
				return;
			}

			const is_array_parameter = raw_key.endsWith( '[]' );
			const field_name = is_array_parameter ? raw_key.slice( 0, -2 ) : raw_key;
			const normalized_field_name = field_name.toLowerCase();

			if (
				field_name.length < 1
				|| this.url_auto_fill_reserved_parameters.has( normalized_field_name )
				|| normalized_field_name.includes( 'password' )
			) {
				return;
			}

			if ( is_array_parameter ) {
				const existing_value = auto_fill_data.get( field_name );
				if ( Array.isArray( existing_value ) ) {
					existing_value.push( value );
				} else {
					// Mixed scalar/array syntax uses the latest shape, then subsequent [] values accumulate.
					auto_fill_data.set( field_name, [value] );
				}
			} else {
				// URLSearchParams iterates in source order, preserving master's last-scalar-wins behavior.
				auto_fill_data.set( field_name, value );
			}
		} );

		return Object.fromEntries( auto_fill_data );
	}

	/**
	 * Convert route URL parameters into route-data payload fields.
	 */
	static getRouteDataFromUrlParameters( query_params, action = null ) {
		if ( !( query_params instanceof URLSearchParams ) ) {
			return {};
		}

		const route_data = {};
		if ( action === 'list' ) {
			const list_name = query_params.get( 'list_name' );
			const filter_data = {};
			// Follow PHP's familiar query-array convention: repeated filter_name[] parameters
			// are arrays and an unsuffixed filter_name is always scalar. Hash routes never reach
			// PHP, but using the same structural shape avoids inventing an escaping language for
			// scalar date expressions whose human-readable grammar legitimately contains commas.

			if ( typeof list_name === 'string' && list_name.length > 0 ) {
				route_data.list_name = list_name;
			}

			// Keep list filter URL state paired with appendRouteDataToUrlParameters() so refresh can rebuild routed list drill-ins.
			query_params.forEach( ( value, key ) => {
				if ( typeof key !== 'string' || !key.startsWith( 'filter_' ) ) {
					return;
				}

				const is_array_parameter = key.endsWith( '[]' );
				const field_name = key.slice( 'filter_'.length, is_array_parameter ? -2 : undefined );
				if ( field_name.length < 1 ) {
					return;
				}

				if ( is_array_parameter ) {
					// URLSearchParams preserves repeated-key order. If a hand-authored route mixes
					// scalar and [] forms, the latest shape wins and subsequent [] values append,
					// matching the router's existing editable-route array behavior.
					filter_data[field_name] = Array.isArray( filter_data[field_name] )
						? [ ...filter_data[field_name], value ]
						: [value];
				} else {
					filter_data[field_name] = value;
				}
			} );

			if ( Object.keys( filter_data ).length > 0 ) {
				route_data.filter_data = filter_data;
			}
		}

		if ( ['edit', 'view', 'new'].includes( action ) ) {
			const edit_name = query_params.get( 'edit_name' );
			const audit_table_name = query_params.get( 'audit_table_name' );

			if ( typeof edit_name === 'string' && edit_name.length > 0 ) {
				route_data.edit_name = edit_name;
			}

			if ( typeof audit_table_name === 'string' && audit_table_name.length > 0 ) {
				route_data.audit_filter_data = {
					table_name: audit_table_name.includes( ',' ) ? audit_table_name.split( ',' ).filter( Boolean ) : audit_table_name,
				};
			}

			if ( ['edit', 'new'].includes( action ) ) {
				const url_auto_fill_data = this.getURLAutoFillDataFromUrlParameters( query_params );
				if ( Object.keys( url_auto_fill_data ).length > 0 ) {
					route_data.url_auto_fill_data = url_auto_fill_data;
				}
			}
		}

		if ( action === 'wizard' ) {
			const wizard_name = query_params.get( 'wizard_name' );

			if ( typeof wizard_name === 'string' && wizard_name.length > 0 ) {
				route_data.wizard_name = wizard_name;
			}
		}

		if ( action === 'shell' ) {
			// Restore the exact container identity without recreating executable task payloads.
			const shell_name = query_params.get( 'shell_name' );

			if ( typeof shell_name === 'string' && shell_name.length > 0 ) {
				route_data.shell_name = shell_name;
			}
		}

		return route_data;
	}

	/**
	 * Apply supported mode flags from compact route URL state.
	 */
	static applyModeFlagsFromUrlParameters( tt_route_data, flags ) {
		if ( !tt_route_data || !flags ) {
			return tt_route_data;
		}

		const supported_mode_flags = Object.keys( tt_route_data.getAllModeFlags?.() ?? {} );
		const requested_mode_flags = flags
			.split( ',' )
			.map( flag => flag.trim() )
			.filter( flag => flag.length > 0 );

		requested_mode_flags.forEach( mode_flag => {
			if ( supported_mode_flags.includes( mode_flag ) ) {
				tt_route_data.enableModeFlag( mode_flag );
			}
		} );

		return tt_route_data;
	}

	/**
	 * Create a complete route-data object from the current URL when no caller-supplied route exists.
	 */
	static createRouteDataFromUrlParameters( query_params, module, action, record_id, flags, tab ) {
		const tt_route_data = this.createRouteData();
		if ( !tt_route_data ) {
			return null;
		}

		return this.updateRouteDataFromUrlParameters( tt_route_data, query_params, module, action, record_id, flags, tab, false );
	}

	/**
	 * Mutate route data from URL state while optionally preserving existing caller-owned payload keys.
	 */
	static updateRouteDataFromUrlParameters( tt_route_data, query_params, module, action, record_id, flags, tab, preserve_data = true ) {
		if ( !tt_route_data ) {
			return null;
		}

		const existing_data = preserve_data ? ( tt_route_data.getData?.() || {} ) : {};
		const url_data = this.getRouteDataFromUrlParameters( query_params, action );

		tt_route_data.setModule( module );
		tt_route_data.setAction( action );
		tt_route_data.setRecordIds( record_id );
		tt_route_data.setTab( tab );
		tt_route_data.setData( {
			...existing_data,
			...url_data,
		} );

		Object.keys( tt_route_data.getAllModeFlags?.() ?? {} ).forEach( mode_flag => {
			if ( typeof tt_route_data.disableModeFlag === 'function' ) {
				tt_route_data.disableModeFlag( mode_flag );
			}
		} );
		this.applyModeFlagsFromUrlParameters( tt_route_data, flags );
		this.updateDeclaredRouteUrlState( tt_route_data, query_params );

		return tt_route_data;
	}

	/**
	 * Update the mounted owner's reactive URL snapshot without interpreting its
	 * domain-specific parameter names.
	 */
	static updateDeclaredRouteUrlState( tt_route_data, path_or_params ) {
		if ( typeof tt_route_data?.setRouteUrlState !== 'function' ) {
			return false;
		}

		const declared_params = RouteSurfacePolicy.getDeclaredUrlStateParams( tt_route_data );
		if ( declared_params.length < 1 ) {
			return false;
		}

		const current_state = tt_route_data.getRouteUrlState?.() || {};
		const next_state = RouteSurfacePolicy.getDeclaredUrlStateSnapshot( path_or_params, tt_route_data );

		if ( JSON.stringify( current_state ) === JSON.stringify( next_state ) ) {
			return false;
		}

		tt_route_data.setRouteUrlState( next_state );
		return true;
	}

	/**
	 * Resolve route data for an in-flight navigation and bind it to an existing stack id.
	 */
	static prepareRouteDataForStackView( tt_route_data, view_id ) {
		if ( !tt_route_data || !view_id ) {
			return tt_route_data;
		}

		tt_route_data.setInstanceId( view_id );
		if ( typeof tt_route_data.setViewStackId === 'function' ) {
			tt_route_data.setViewStackId( view_id );
		}

		return tt_route_data;
	}

	/**
	 * Append route-data payload fields to URL parameters.
	 */
	static appendRouteDataToUrlParameters( params, tt_route_data ) {
		if ( !( params instanceof URLSearchParams ) || !tt_route_data || typeof tt_route_data.getAction !== 'function' ) {
			return params;
		}

		const tab = Number( tt_route_data.getTab?.() );
		if ( Number.isInteger( tab ) && tab > 0 ) {
			// Tab zero is the URL's implicit default; non-default tab state must be explicit so in-memory report/edit routes survive refresh and browser history reconstruction.
			params.set( 'tab', String( tab ) );
		}

		if ( tt_route_data.getAction() === 'list' ) {
			const route_data = tt_route_data.getData?.() || {};
			const list_name = route_data?.list_name;
			const filter_data =
				( route_data.filter_data && typeof route_data.filter_data === 'object' && !Array.isArray( route_data.filter_data ) && route_data.filter_data ) ||
				( route_data.list_api_filters?.filter_data && typeof route_data.list_api_filters.filter_data === 'object' && !Array.isArray( route_data.list_api_filters.filter_data ) && route_data.list_api_filters.filter_data ) ||
				null;

			if ( typeof list_name === 'string' && list_name.length > 0 ) {
				params.set( 'list_name', list_name );
			}

			// Route-data serialization replaces the complete list-filter object, so reused
			// URLSearchParams must not retain fields from the prior list state. Clear every
			// parser-owned filter_* key before writing the replacement; doing this centrally
			// also removes repeated [] keys whose field is absent from filter_data.
			[ ...params.keys() ].forEach( param_name => {
				if ( param_name.startsWith( 'filter_' ) ) {
					params.delete( param_name );
				}
			} );
			if ( filter_data ) {
				Object.entries( filter_data ).forEach( ( [key, value] ) => {
					if ( typeof key !== 'string' || key.length === 0 || value === undefined || value === null || value === '' ) {
						return;
					}

					// Route callers use both raw field names and already-prefixed filter keys. Normalize
					// once so parameter names match the read path and repeated serialization never
					// produces filter_filter_* parameters.
					const field_name = key.startsWith( 'filter_' ) ? key.slice( 'filter_'.length ) : key;
					const param_name = `filter_${field_name}`;

					if ( Array.isArray( value ) ) {
						// Repeated [] parameters preserve array shape independently of item content, so
						// commas remain ordinary data and values do not require custom escaping.
						value
							.filter( item => item !== undefined && item !== null && item !== '' )
							.forEach( item => params.append( `${param_name}[]`, String( item ) ) );

						return;
					}

					if ( typeof value === 'object' ) {
						// Nested filter objects have no stable query-string grammar here. Ignore them
						// rather than emitting "[object Object]" and corrupting browser-history state.
						return;
					}

					params.set( param_name, String( value ) );
				} );
			}
		}

		if ( ['edit', 'view', 'new'].includes( tt_route_data.getAction() ) ) {
			const route_data = tt_route_data.getData?.() || {};
			const edit_name = route_data?.edit_name;
			const audit_table_name = route_data?.audit_filter_data?.table_name;

			if ( typeof edit_name === 'string' && edit_name.length > 0 ) {
				params.set( 'edit_name', edit_name );
			}

			// Audit detail routes need the parent audit table in the URL so browser Back can recreate APILog filters after the in-memory route payload is gone.
			if ( tt_route_data.getModule?.() === 'AuditDetail' ) {
				if ( Array.isArray( audit_table_name ) ) {
					const serialized_table_name = audit_table_name.filter( item => typeof item === 'string' && item.length > 0 ).join( ',' );
					if ( serialized_table_name.length > 0 ) {
						params.set( 'audit_table_name', serialized_table_name );
					}
				} else if ( typeof audit_table_name === 'string' && audit_table_name.length > 0 ) {
					params.set( 'audit_table_name', audit_table_name );
				}
			}
		}

		if ( tt_route_data.getAction() === 'wizard' ) {
			const route_data = tt_route_data.getData?.();
			const wizard_name = route_data?.wizard_name;

			if ( typeof wizard_name === 'string' && wizard_name.length > 0 ) {
				params.set( 'wizard_name', wizard_name );
			}
		}

		if ( tt_route_data.getAction() === 'shell' ) {
			// Round-trip the structural selector; owner callbacks are deliberately not serialized.
			const route_data = tt_route_data.getData?.();
			const shell_name = route_data?.shell_name;

			if ( typeof shell_name === 'string' && shell_name.length > 0 ) {
				params.set( 'shell_name', shell_name );
			}
		}

		return params;
	}

	/**
	 * Stringify object props for router transport.
	 */
	static convertToRouterProps( object ) {
		return JSON.stringify( object );
	}

	/**
	 * Parse stringified object props from router transport.
	 */
	static convertFromRouterProps( stringified_object_from_router ) {
		return JSON.parse( stringified_object_from_router );
	}

	/**
	 * Ensure the current browser entry owns a safe TimeTrex Back boundary.
	 *
	 * Vue Router initializes its own state before this runs. Merge our depth into
	 * that object so reload preserves an existing cursor while a first entry starts
	 * at zero without replacing Vue Router's position/back/forward fields.
	 */
	static initializeAppHistoryState() {
		const app_history_depth = getAppHistoryDepth( window.history.state );
		const current_timestamp_param = Number( this.getTimestampParam( window.location.hash ) );
		if ( Number.isSafeInteger( current_timestamp_param ) && current_timestamp_param >= 0 ) {
			// A reload can restore a `t` value newer than this fresh module instance.
			// Seed the issuer so the first post-reload push cannot reuse that identity.
			this.last_timestamp_param = current_timestamp_param;
		}
		window.history.replaceState(
			buildAppHistoryState( window.history.state, app_history_depth ),
			'',
			window.location.href
		);
	}

	/**
	 * Return the current entry's safe number of TimeTrex-owned Back steps.
	 */
	static getAppHistoryLength() {
		return getAppHistoryDepth( window.history.state );
	}

	/**
	 * Return the current Vue route name.
	 */
	static getCurrentRouteName() {
		return this.getRouter()?.currentRoute?.value?.name;
	}

	/**
	 * Add a compact monotonic timestamp parameter to force route uniqueness.
	 *
	 * Whole-second timestamps can collide when two router pushes overlap. Advancing
	 * from the last issued value lets the existing `t` field also identify the
	 * matching in-memory navigation request without adding another URL parameter.
	 */
	static addTimestampParam( path ) {
		const params = this.getUrlParameters( path );
		this.last_timestamp_param = getNextTimestampParam(
			Math.floor( Date.now() / 1000 ),
			this.last_timestamp_param
		);
		params.set( 't', this.last_timestamp_param.toString() );

		return params.toString();
	}

	/**
	 * Register payload that belongs only to one exact programmatic target.
	 */
	static registerNavigationRequest( target_path, navigation_request ) {
		const request_key = getNavigationRequestKey( target_path );
		if ( request_key.length > 0 ) {
			this.navigation_requests.set( request_key, navigation_request );
		}

		return request_key;
	}

	/**
	 * Resolve a programmatic request; browser Back/Forward intentionally returns null.
	 */
	static getNavigationRequest( target_path ) {
		return this.navigation_requests.get( getNavigationRequestKey( target_path ) ) ?? null;
	}

	/**
	 * Clear request payload after route completion or a rejected/cancelled push.
	 */
	static deleteNavigationRequest( target_path ) {
		const request_key = getNavigationRequestKey( target_path );
		if ( request_key.length > 0 ) {
			this.navigation_requests.delete( request_key );
		}
	}

	/**
	 * Return the timestamp parameter from a route-like path.
	 */
	static getTimestampParam( path ) {
		const params = this.getUrlParameters( path );

		return params.get( 't' ) ?? null;
	}

	/**
	 * Remove the timestamp parameter from a route-like path.
	 */
	static stripTimestampParam( path ) {
		const params = this.getUrlParameters( path );
		params.delete( 't' );

		return params.toString();
	}

	/**
	 * Parse route-like paths, hashes, and bare query strings into parameters.
	 */
	static getUrlParameters( path ) {
		if ( typeof path !== 'string' ) {
			Debug.Text( 'Unable to parse URL parameters: Invalid path.', 'TTRouter.js', 'TTRouter', 'getUrlParameters', 10 );
			return new URLSearchParams();
		}

		if ( path.startsWith( '#/' ) ) {
			path = path.slice( 2 );
		} else if ( path.startsWith( '#' ) ) {
			path = path.slice( 1 );
		} else if ( path.startsWith( '/' ) ) {
			path = path.slice( 1 );
		}

		return new URLSearchParams( path );
	}

	/**
	 * Check whether an existing stack view owns the same routed surface.
	 */
	static isSameRouteSurface( TTViewStackStore, view, module, action ) {
		if ( !view?.tt_route_data || TTViewStackStore?.isSidePanelView?.( view ) === true ) {
			return false;
		}

		return view.tt_route_data.getModule?.() === module
			&& view.tt_route_data.getAction?.() === action;
	}

	/**
	 * Prefer the active view for same-surface updates, then the normalized primary source.
	 *
	 * If neither owns the route, list routes may reuse another existing primary
	 * stack item for the same module/action. This keeps menu/history navigation
	 * from creating duplicate list DOM. We deliberately do not do the broad stack
	 * search for edit/new/view routes because those can carry record-specific
	 * defaults, unsaved field state, or "new record" setup that must not be reused
	 * just because the module/action strings match.
	 */
	static getSameSurfaceStackView( TTViewStackStore, active_view, source_view, module, action ) {
		if ( this.isSameRouteSurface( TTViewStackStore, active_view, module, action ) ) {
			return active_view;
		}

		if ( source_view?.id !== active_view?.id && this.isSameRouteSurface( TTViewStackStore, source_view, module, action ) ) {
			return source_view;
		}

		if ( action !== 'list' ) {
			return null;
		}

		for ( let i = TTViewStackStore.view_stack.length - 1; i >= 0; i-- ) {
			const candidate = TTViewStackStore.view_stack[i];
			if ( this.isSameRouteSurface( TTViewStackStore, candidate, module, action ) ) {
				return candidate;
			}
		}

		return null;
	}

	/**
	 * Update a stack entry for a same module/action navigation without adding another DOM instance.
	 *
	 * Rebuild means "same stack/window slot, fresh component instance". No rebuild
	 * means "same stack/window slot and same mounted component", used for local
	 * URL-only updates where the view already handled its own data change.
	 */
	static updateSameSurfaceStackView( TTViewStackStore, view, tt_route_data, path, options = {} ) {
		if ( !TTViewStackStore || !view || !tt_route_data ) {
			return false;
		}

		this.prepareRouteDataForStackView( tt_route_data, view.id );

		view.component = markRaw( this.getTTViewLoaderComponent() );
		view.tt_route_data = tt_route_data;
		view.path = path;

		if ( options?.rebuild === true ) {
			TTViewStackStore.bumpRouteRevision( view.id );
		}

		return true;
	}

	/**
	 * Decide whether same module/action navigation should keep the mounted DOM.
	 *
	 * Explicit URL-only update paths pass `same_surface_rebuild: false`; cancel
	 * return paths also preserve the parent list. Explicit TTRouteData navigation
	 * rebuilds because its in-memory payload is consumed during destination
	 * initialization and is not represented by the URL structural key. Otherwise
	 * RouteSurfacePolicy compares structural params, avoiding flashes for URL-owned
	 * record/tab/date state while rebuilding different list/edit definitions.
	 */
	static shouldPreserveSameSurfaceDOM( view, path, action, navigation_options = {}, returning_from_modal_to_parent_list = false, next_route_data = null ) {
		if ( navigation_options.same_surface_rebuild === false || returning_from_modal_to_parent_list ) {
			return true;
		}

		if ( next_route_data ) {
			return false;
		}

		return RouteSurfacePolicy.shouldPreserveSameSurfaceDOM( {
			current_path: view?.path ?? '',
			next_path: path ?? '',
			action,
			current_route_data: view?.tt_route_data ?? null,
			next_route_data,
		} );
	}

	/**
	 * Navigate from TTRouteData by binding it to this individual router push.
	 */
	static navigate( tt_route_data, options = {} ) {
		let pin_record_source_view_id = null;
		if ( options?.pin_record_source_right === true ) {
			const TTViewStackStore = this.getViewStackStore();
			const source_view = TTViewStackStore?.getViewById?.( this.getParentViewIdFromTTRoute( tt_route_data, TTViewStackStore.active_view_id ) );

			// Repro this protects: Request Authorization -> View -> Jump To ->
			// TimeSheet. Capture only record routes, not list-origin Jump To; pinning
			// after routing lets the new primary anchor direct-open records and keeps
			// an occupied/dirty pane mounted instead of replacing it.
			if ( ['view', 'edit'].includes( source_view?.tt_route_data?.getAction?.() ) && TTViewStackStore.isSidePanelView( source_view ) !== true ) {
				pin_record_source_view_id = source_view.id;
			}
		}

		let path = this.getPathFromTTRoute( tt_route_data );
		const mounted_return_state_view = this.getMountedReturnStateViewForNavigation(
			tt_route_data?.getModule?.(),
			tt_route_data?.getAction?.(),
			tt_route_data,
			path,
			options
		);

		if ( mounted_return_state_view ) {
			return this.navigatePath( this.getMountedReturnStateViewPath( mounted_return_state_view, path ), {
				...options,
				same_surface_rebuild: false,
				pin_record_source_view_id: pin_record_source_view_id,
			} );
		}

		path = this.applyReturnStateToRouteNavigation( tt_route_data, path, options );

		if ( typeof path !== 'string' || path.length === 0 ) {
			return;
		}

		// Returning the Vue Router promise lets semantic presentation helpers report
		// the stack id assigned by processRoute() without changing fire-and-forget
		// callers that ignore the result.
		return this.navigatePath( path, {
			...options,
			pin_record_source_view_id: pin_record_source_view_id,
			tt_route_data: tt_route_data,
		} );
	}

	/**
	 * Navigate to a route-like path using the shared Vue router.
	 */
	static navigatePath( path, options = {} ) {
		if ( typeof path !== 'string' || path.length === 0 ) {
			Debug.Text( 'Unable to navigate: Missing path.', 'TTRouter.js', 'TTRouter', 'navigatePath', 10 );
			return;
		}

		const path_params = this.getUrlParameters( path );
		const mounted_return_state_view = this.getMountedReturnStateViewForNavigation(
			path_params.get( 'm' ),
			path_params.get( 'a' ) || 'list',
			null,
			path,
			options
		);

		if ( mounted_return_state_view ) {
			options = {
				...options,
				same_surface_rebuild: false,
			};
			path = this.getMountedReturnStateViewPath( mounted_return_state_view, path );
		} else {
			path = this.applyReturnStateToPathNavigation( path, options );
		}

		path = this.addTimestampParam( path );
		let current_path = window.location.hash || '';

		if ( current_path.startsWith( '#/' ) ) {
			current_path = current_path.slice( 2 );
		} else if ( current_path.startsWith( '#' ) ) {
			current_path = current_path.slice( 1 );
		}

		// A restored URL can carry a timestamp at or ahead of this module's local
		// issuer. Advance and retain it because Vue Router ignores identical paths and
		// would otherwise skip the new in-memory right-pane metadata.
		if ( options?.force_navigation === true && this.stripTimestampParam( current_path ) === this.stripTimestampParam( path ) && this.getTimestampParam( current_path ) === this.getTimestampParam( path ) ) {
			const forced_path_params = this.getUrlParameters( path );
			this.last_timestamp_param = getNextTimestampParam(
				Number( forced_path_params.get( 't' ) ) + 1,
				this.last_timestamp_param
			);
			forced_path_params.set( 't', this.last_timestamp_param.toString() );
			path = forced_path_params.toString();
		}

		// Issue #4005: Save & New supplies fresh route data even when a copied draft
		// already has the same New URL. Let destination initialization rebuild that
		// form; URL-only repeats and other same-route actions keep their mounted state.
		if ( options?.force_navigation !== true && options?.tt_route_data?.getAction?.() !== 'new' && this.stripTimestampParam( current_path ) === this.stripTimestampParam( path ) ) {
			return;
		}

		const router = this.getRouter();
		if ( !router ) {
			return;
		}

		// URL-only stack fallback can intentionally move window.location away from
		// Vue Router's currentRoute. A rapid reopen within addTimestampParam()'s
		// one-second resolution would otherwise push the exact internal route again,
		// so advance `t` before Vue Router rejects it as duplicated navigation.
		if ( this.getUrlParameters( router.currentRoute?.value?.fullPath ?? '' ).toString() === this.getUrlParameters( path ).toString() ) {
			const unique_path_params = this.getUrlParameters( path );
			this.last_timestamp_param = getNextTimestampParam(
				Number( unique_path_params.get( 't' ) ) + 1,
				this.last_timestamp_param
			);
			unique_path_params.set( 't', this.last_timestamp_param.toString() );
			path = unique_path_params.toString();
		}

		const navigation_options = {
			same_surface_rebuild: options?.same_surface_rebuild !== false,
			collapse_source: options?.collapse_source === true,
			pin_record_source_view_id: options?.pin_record_source_view_id ?? null,
			// A workflow can keep a modal mounted while routing to an already-mounted
			// list, then collapse it after the route succeeds. Without this explicit
			// identity, same-surface reuse mistakes that transition for Cancel/return
			// and removes the modal before its suspended state can be preserved.
			preserve_source_view_id: typeof options?.preserve_source_view_id === 'string'
				? options.preserve_source_view_id
				: null,
		};
		if ( typeof options?.refresh_on_return === 'boolean' ) {
			navigation_options.refresh_on_return = options.refresh_on_return;
		}
		const target_path = path.startsWith( '/' ) ? path : '/' + path;
		const navigation_request = {
			navigation_options: navigation_options,
			target_path: target_path,
			tt_route_data: options?.tt_route_data ?? null,
		};
		this.registerNavigationRequest( target_path, navigation_request );

		const router_push_promise = router.push( {
			path: target_path,
			// Depth is entry-owned: Back/Forward restores the value for that exact
			// entry, while Back-then-push branches from the restored current depth.
			state: {
				[APP_HISTORY_DEPTH_STATE_KEY]: this.getAppHistoryLength() + 1,
			},
		} ).catch( error => {
			Debug.Text( 'Router push failed: ' + ( error?.message ?? error ), 'TTRouter.js', 'TTRouter', 'navigatePath', 10 );
		} );
		const navigation_promise = router_push_promise.finally( () => {
			// afterEach normally consumes this first; finally also covers thrown guards,
			// rejected pushes, and redirects that commit a different target path.
			this.deleteNavigationRequest( target_path );
			this.pending_navigation_promises.delete( navigation_promise );
		} );
		this.pending_navigation_promises.add( navigation_promise );

		return navigation_promise;
	}

	/**
	 * Wait for every Vue Router push currently started by synchronous navigation helpers.
	 * Callers should first await any action that can perform asynchronous work before it starts routing.
	 */
	static waitForPendingNavigation() {
		return Promise.all( Array.from( this.pending_navigation_promises ) );
	}

	/**
	 * Replace a #/ hash through the history driver without running route guards or remounting views.
	 */
	static replaceHistoryHashPreservingRouterState( new_hash ) {
		if ( typeof new_hash !== 'string' || new_hash.length === 0 ) {
			Debug.Text( 'Unable to update URL hash: Missing hash.', 'TTRouter.js', 'TTRouter', 'replaceHistoryHashPreservingRouterState', 10 );
			return;
		}

		// Restoring Accruals after Jump To TimeSheet can change the visible window without
		// navigating. Native replaceState alone leaves Vue Router's current entry/location
		// on TimeSheet; the next push rewrites the restored Accruals entry to that stale
		// route, adding another apparent no-op Back step on each restore/jump cycle.
		// The history driver synchronizes both cursors and retains position, back/forward,
		// and custom state (including TimeTrex history depth). Preserve saved scroll too.
		// Do not pass the entire old state as data: its stale current would override the fix.
		this.getRouter().options.history.replace( new_hash.slice( 1 ), { scroll: window.history.state?.scroll ?? null } );
	}

	/**
	 * Sync the active primary view path after URL-only updates.
	 */
	static syncActiveViewPath( path ) {
		const TTViewStackStore = this.getViewStackStore();
		if ( !TTViewStackStore ) {
			return;
		}

		const active_view = TTViewStackStore.getViewById( TTViewStackStore.active_view_id );
		const normalized_path = typeof path === 'string' ? this.getUrlParameters( path ).toString() : '';

		if ( !active_view || TTViewStackStore.isSidePanelView( active_view ) || normalized_path.length === 0 ) {
			return;
		}

		active_view.path = '/' + normalized_path;
	}

	/**
	 * Replace the hash path without navigating and return the normalized path.
	 */
	static replacePathWithoutNavigation( path ) {
		if ( typeof path !== 'string' || path.length === 0 ) {
			Debug.Text( 'Unable to update URL path: Missing path.', 'TTRouter.js', 'TTRouter', 'replacePathWithoutNavigation', 10 );
			return '';
		}

		const next_path = this.addTimestampParam( this.getUrlParameters( path ).toString() );

		this.replaceHistoryHashPreservingRouterState( '#/' + next_path );
		if ( next_path.length > 0 ) {
			this.syncActiveViewPath( next_path );
		}

		return next_path;
	}

	/**
	 * Update the owning stack URL from TTRouteData without navigating.
	 *
	 * Active views replace the visible browser URL. Inactive and pinned views only
	 * update their own remembered path so they cannot overwrite the active view's
	 * route before its next URL-driven navigation.
	 */
	static updateUrlFromTTRoute( tt_route_data ) {
		if ( !tt_route_data || typeof tt_route_data.getAction !== 'function' ) {
			Debug.Text( 'Unable to update URL: Missing TTRouteData for target view.', 'TTRouter.js', 'TTRouter', 'updateUrlFromTTRoute', 10 );
			return '';
		}

		const path = this.getPathFromTTRoute( tt_route_data );
		if ( typeof path !== 'string' || path.length === 0 ) {
			return '';
		}

		const TTViewStackStore = this.getViewStackStore();
		const target_options = { tt_route_data: tt_route_data };
		const target_view_id = this.getUrlUpdateTargetViewId( target_options );
		const target_view = this.getUrlUpdateTargetView( TTViewStackStore, target_options );

		if ( target_view_id && !target_view ) {
			Debug.Text( 'Skipped URL update for missing stack view id=' + target_view_id, 'TTRouter.js', 'TTRouter', 'updateUrlFromTTRoute', 10 );
			return '';
		}

		// Save & Continue can run inside a pinned editor while another module owns the
		// primary workspace. Keep the saved editor's new route on its own stack item;
		// otherwise the primary view's next arrow/navigation reopens the pinned editor.
		if ( target_view && target_view.id !== TTViewStackStore?.active_view_id ) {
			return this.updateInactiveStackViewURL( target_view, path );
		}

		return this.replacePathWithoutNavigation( path );
	}

	/**
	 * Convert TTRouteData into the route path used by the hash router.
	 */
	static getPathFromTTRoute( tt_route_data ) {
		if ( !tt_route_data || typeof tt_route_data.getAction !== 'function' ) {
			Debug.Error( 'Unable to parse tt_route_data for a url.', 'TTRouter.js', 'TTRouter', 'getPathFromTTRoute', 1 );
			return null;
		}

		let path = null;

		if ( tt_route_data.getAction() === 'list' ) {
			path = this.getListViewPath( tt_route_data.getModule() );
		} else if ( tt_route_data.getAction() === 'edit' ) {
			if ( tt_route_data.getModeFlagStatus( 'mass' ) ) {
				path = this.getMassEditViewPath( tt_route_data.getModule(), tt_route_data.getRecordIds() );
			} else {
				path = this.getEditViewPath( tt_route_data.getModule(), tt_route_data.getRecordIds() );
			}
		} else if ( tt_route_data.getAction() === 'view' ) {
			path = this.getViewPath( tt_route_data.getModule(), tt_route_data.getRecordIds() );
		} else if ( tt_route_data.getAction() === 'new' ) {
			path = this.getNewViewPath( tt_route_data.getModule() );
		} else if ( tt_route_data.getAction() === 'report' ) {
			path = this.getReportViewPath( tt_route_data.getModule(), tt_route_data.getRecordIds() );
		} else if ( tt_route_data.getAction() === 'shell' ) {
			path = this.getShellViewPath( tt_route_data.getModule(), tt_route_data.getRecordIds() );
		} else if ( tt_route_data.getAction() === 'wizard' ) {
			path = this.getWizardViewPath( tt_route_data.getModule(), tt_route_data.getRecordIds() );
		} else {
			Debug.Error( 'Unable to parse tt_route_data for a url.', 'TTRouter.js', 'TTRouter', 'getPathFromTTRoute', 1 );
			return null;
		}

		const params = this.getUrlParameters( path );
		this.appendRouteDataToUrlParameters( params, tt_route_data );

		return params.toString();
	}

	/**
	 * Build a list route path.
	 */
	static getListViewPath( module ) {
		return `m=${module}&a=list`;
	}

	/**
	 * Build an edit route path.
	 */
	static getEditViewPath( module, record_id ) {
		let display_id;

		if ( Array.isArray( record_id ) ) {
			if ( record_id.length > 1 ) {
				Debug.Error( 'Fatal Error. Multiple record IDs detected. Unable to determine which record to edit.', 'TTRouter.js', 'TTRouter', 'getEditViewPath', 1 );
			}

			display_id = record_id[0];
		} else {
			display_id = record_id;
		}

		if ( display_id === undefined || display_id === null || display_id === '' ) {
			return `m=${module}&a=edit`;
		}

		return `m=${module}&a=edit&id=${display_id}`;
	}

	/**
	 * Build a view route path.
	 */
	static getViewPath( module, record_id ) {
		let display_id;

		if ( Array.isArray( record_id ) ) {
			if ( record_id.length > 1 ) {
				Debug.Error( 'Fatal Error. Multiple record IDs detected. Unable to determine which record to view.', 'TTRouter.js', 'TTRouter', 'getViewPath', 1 );
			}

			display_id = record_id[0];
		} else {
			display_id = record_id;
		}

		if ( display_id === undefined || display_id === null || display_id === '' ) {
			return `m=${module}&a=view`;
		}

		return `m=${module}&a=view&id=${display_id}`;
	}

	/**
	 * Build a report route path.
	 */
	static getReportViewPath( module, record_id ) {
		let display_id;

		if ( Array.isArray( record_id ) ) {
			if ( record_id.length > 1 ) {
				Debug.Error( 'Fatal Error. Multiple record IDs detected. Unable to determine which record to edit.', 'TTRouter.js', 'TTRouter', 'getReportViewPath', 1 );
			}

			display_id = record_id[0];
		} else {
			display_id = record_id;
		}

		if ( display_id === undefined || display_id === null || display_id === '' ) {
			return `m=${module}&a=report`;
		}

		return `m=${module}&a=report&id=${display_id}`;
	}

	/**
	 * Build a wizard route path.
	 */
	static getWizardViewPath( module, record_id ) {
		let display_id;

		if ( Array.isArray( record_id ) ) {
			if ( record_id.length > 1 ) {
				Debug.Error( 'Fatal Error. Multiple record IDs detected. Unable to determine which wizard record to open.', 'TTRouter.js', 'TTRouter', 'getWizardViewPath', 1 );
			}

			display_id = record_id[0];
		} else {
			display_id = record_id;
		}

		if ( display_id === undefined || display_id === null || display_id === '' ) {
			return `m=${module}&a=wizard`;
		}

		return `m=${module}&a=wizard&id=${display_id}`;
	}

	/**
	 * Build a standalone shell route path. Shell identity is serialized separately
	 * as shell_name so module continues to identify the schema/API owner.
	 */
	static getShellViewPath( module, record_id ) {
		let display_id;

		if ( Array.isArray( record_id ) ) {
			if ( record_id.length > 1 ) {
				Debug.Error( 'Fatal Error. Multiple record IDs detected. Unable to determine which shell record to open.', 'TTRouter.js', 'TTRouter', 'getShellViewPath', 1 );
			}

			display_id = record_id[0];
		} else {
			display_id = record_id;
		}

		if ( display_id === undefined || display_id === null || display_id === '' ) {
			return `m=${module}&a=shell`;
		}

		return `m=${module}&a=shell&id=${display_id}`;
	}

	/**
	 * Build a new route path.
	 */
	static getNewViewPath( module ) {
		return `m=${module}&a=new`;
	}

	/**
	 * Build a mass-edit route path.
	 */
	static getMassEditViewPath( module ) {
		return `m=${module}&a=edit&f=mass`;
	}

	/**
	 * Resolve the return owner, not the refresh target or current active window.
	 * Issue #3924: Nested editors can have a precise live parent while placement has
	 * a broader source. Keep that parent first so unsaved form state survives Cancel.
	 * A route-less host (Dashboard) or restored route may have only source_view_id;
	 * it still identifies a mounted window and must work without getParentView().
	 * The final fallback is caller-owned (for example Save's changed-row target),
	 * never an instruction to infer ownership from whichever pane has focus.
	 */
	static getParentViewIdFromTTRoute( tt_route_data, fallback_parent_view_id = null ) {
		const parent_route_data = tt_route_data?.getParentView?.()?.getTTRouteData?.();
		const source_view_id = tt_route_data?.getData?.()?.view_stack?.source_view_id ?? null;

		if ( !parent_route_data ) {
			// Issue #3924: Modal and right-pane callers may only have the launching window ID.
			// A live parent still wins when return ownership differs from placement or refresh.
			return source_view_id ?? fallback_parent_view_id;
		}

		return parent_route_data?.getViewStackId?.() ?? parent_route_data?.getInstanceId?.() ?? source_view_id ?? fallback_parent_view_id;
	}

	/**
	 * Modal placement normalizes the visual source back to a primary list, but a
	 * modal opened from another modal must still cancel back to that modal.
	 */
	static preserveActiveModalAsRouteParent( tt_route_data, active_view, target_action ) {
		const route_action = tt_route_data?.getAction?.() ?? target_action;
		const route_view_stack = tt_route_data?.getData?.()?.view_stack;
		const route_requests_modal_view = route_view_stack && typeof route_view_stack === 'object' && Array.isArray( route_view_stack ) !== true && route_view_stack.is_modal_view === true && route_view_stack.is_side_panel !== true;
		const active_is_side_panel = active_view?.is_side_panel === true || active_view?.tt_route_data?.getData?.()?.view_stack?.is_side_panel === true;
		const active_is_modal_view = active_view?.is_modal_view === true;

		if (
			!tt_route_data ||
			!active_view ||
			active_is_side_panel === true ||
			this.isModalRouteSurface( tt_route_data, route_action ) !== true ||
			active_is_modal_view !== true ||
			// Reused modal routes, such as Save & Copy, already carry their intended parent. Inheriting the active modal here would make the copied route return to the source edit.
			( route_view_stack && typeof route_view_stack === 'object' && Array.isArray( route_view_stack ) !== true && route_view_stack.preserve_active_modal_parent === false ) ||
			typeof tt_route_data.setParentView !== 'function'
		) {
			return tt_route_data;
		}

		const existing_parent_view = tt_route_data.getParentView?.();
		const existing_parent_route_data = existing_parent_view?.getTTRouteData?.();
		const existing_parent_view_id = existing_parent_route_data?.getViewStackId?.()
			?? existing_parent_route_data?.getInstanceId?.()
			?? null;
		const existing_parent_is_live_view_reference = existing_parent_route_data?.getViewReference?.() === existing_parent_view
			&& typeof existing_parent_view?.handleParentRefreshRequest === 'function';
		const active_view_reference = active_view.tt_route_data?.getViewReference?.();

		if ( existing_parent_view_id && ( route_requests_modal_view !== true || !active_view_reference || existing_parent_view_id === active_view.id ) ) {
			// Nested edit/view routes can already carry a precise parent. Keep that route-owned parent so Cancel returns to the nested screen instead of the currently active modal.
			return tt_route_data;
		}

		if ( active_view_reference ) {
			if ( existing_parent_is_live_view_reference && existing_parent_view !== active_view_reference ) {
				// Embedded sub-list routes use their live list loader as the refresh target, but the modal route
				// parent must remain the edit shell so Save/Cancel returns to the open parent record. Keep this
				// live Vue reference out of the persisted route payload that TTViewStack serializes.
				tt_route_data.setRefreshParentView( existing_parent_view );
			}

			tt_route_data.setParentView( active_view_reference );
		}

		return tt_route_data;
	}

	/**
	 * Resolve explicit return path from route data by key order.
	 */
	static getExplicitRouteReturnPath( tt_route_data, path_keys = [] ) {
		const route_data = tt_route_data?.getData?.() ?? {};

		for ( const path_key of path_keys ) {
			const explicit_return_path = route_data?.[path_key];

			if ( typeof explicit_return_path === 'string' && explicit_return_path.length > 0 ) {
				return explicit_return_path;
			}
		}

		return null;
	}

	/**
	 * Surface a stack view by id.
	 */
	static returnToViewById( view_id, options = {} ) {
		const TTViewStackStore = this.getViewStackStore();

		if ( !TTViewStackStore || !view_id || !TTViewStackStore.getViewById( view_id ) ) {
			return false;
		}

		TTViewStackStore.showView( view_id, options );
		return true;
	}

	/**
	 * Return through stack-aware navigation before falling back to browser history.
	 */
	static returnToPreviousView( action = null, options = {} ) {
		const allow_browser_history = options?.allow_browser_history !== false;
		const TTViewStackStore = this.getViewStackStore();
		const returned_to_stack_view = TTViewStackStore?.returnToPreviousView?.( action, options );

		if ( returned_to_stack_view ) {
			return true;
		}

		if ( action === 'list' && TTViewStackStore ) {
			const active_view = TTViewStackStore.getViewById( TTViewStackStore.active_view_id );
			const active_route_data = active_view?.tt_route_data;

			if ( active_route_data?.getModule?.() && active_route_data.getAction?.() !== 'list' ) {
				this.navigateToListViewFromTTRouteData( active_route_data );
				return true;
			}
		}

		if ( allow_browser_history && this.getAppHistoryLength() > 0 ) {
			window.history.back();
			return true;
		}

		return false;
	}

	/**
	 * Return from a route to explicit parent/previous targets in priority order.
	 * Issue #3924: Both shared editor Save and Cancel use this operation so a
	 * route-less launcher needs no Dashboard-specific return adapter. Explicit path
	 * context wins, then a mounted parent ID, then the existing stack/history fallback.
	 * Showing by ID preserves the parent's DOM, filters, selection and dirty fields;
	 * constructing a fresh same-module list is not an equivalent return operation.
	 * This helper does not save data or choose which list receives refresh_ids.
	 * Callers must finish their save/refresh pipeline and exit-policy checks first
	 * (notably side-panel close, stacked modal fallback and default destinations).
	 */
	static returnToRouteParentOrPreviousView( tt_route_data, options = {} ) {
		const return_options = {
			refresh_on_return: false,
			...options,
		};
		const TTViewStackStore = this.getViewStackStore();
		const explicit_return_path = return_options?.explicit_return_path
			?? this.getExplicitRouteReturnPath( tt_route_data, return_options?.explicit_path_keys ?? [] );
		// Save can intentionally target a refreshed list/sublist while Cancel still belongs to a dirty source editor. An explicit return target must therefore win here; treating it as a fallback reopens the source editor after the copied record is already saved.
		const parent_view_id = return_options?.parent_view_id ?? this.getParentViewIdFromTTRoute( tt_route_data );
		const current_view_id = tt_route_data?.getViewStackId?.() ?? tt_route_data?.getInstanceId?.() ?? null;
		const explicit_return_navigation_options = {};
		if ( typeof return_options?.refresh_on_return === 'boolean' ) {
			explicit_return_navigation_options.refresh_on_return = return_options.refresh_on_return;
		}
		// Issue #3924: Close only after a mounted-parent return succeeds. Removing the child
		// first can expose the wrong previous window when the intended parent disappeared.
		// Do not remove the parent itself when a reused route shares that stack identity.
		const removeCurrentChildView = () => {
			if (
				!TTViewStackStore ||
				!current_view_id ||
				current_view_id === parent_view_id ||
				!TTViewStackStore.getViewById( current_view_id )
			) {
				return;
			}

			TTViewStackStore.removeView( current_view_id, false );
		};

		if ( explicit_return_path && parent_view_id && this.returnToViewById( parent_view_id, {
			...return_options,
			sync_url: false,
		} ) ) {
			removeCurrentChildView();
			// Issue #3924: An explicit path can carry employee/date/filter context absent
			// from the mounted window's current URL. Restore its window first, then apply
			// that path without rebuilding the form/list that owns retained local state.
			// Push the parent route as a real history entry, but keep the mounted
			// parent DOM. Browser Back should replay edit -> parent -> edit -> parent
			// instead of skipping the parent state between repeated edit/cancel flows.
			this.navigatePath( explicit_return_path, {
				...explicit_return_navigation_options,
				same_surface_rebuild: false,
			} );
			return true;
		}

		if ( explicit_return_path ) {
			// Issue #3924: Deep links and missing parents still honor a workflow's explicit
			// destination. An ID-only return must not override an authored path contract.
			this.navigatePath( explicit_return_path, explicit_return_navigation_options );
			return true;
		}

		if ( parent_view_id && this.returnToViewById( parent_view_id, return_options ) ) {
			removeCurrentChildView();
			return true;
		}

		// Issue #3924: A source ID is not a reconstruction recipe. If the source no
		// longer exists (including Dashboard after document reload), retain the ordinary
		// previous-view/history fallback rather than inventing a module or return URL.
		return this.returnToPreviousView( return_options?.action ?? null, return_options );
	}

	/**
	 * Navigate to a list view from an existing route data object.
	 */
	static navigateToListViewFromTTRouteData( tt_route_data ) {
		const route_data = this.createRouteData();

		if ( !route_data ) {
			return false;
		}

		this.navigate( route_data
			.setModule( tt_route_data.getModule() )
			.setAction( 'list' ) );

		return true;
	}

	/**
	 * Return previous stack view data when available.
	 */
	static getPreviousViewData() {
		return this.getViewStackStore()?.getPreviousViewData?.() ?? null;
	}

	/**
	 * Launch trusted, imported custom content without a route or schema request.
	 * The definition contains presentation only; credentials and callbacks belong
	 * in owner_props and never enter TTViewStack or its saved-layout serialization.
	 * Owners expose close() and optionally canCloseView() for manager Escape. Routed
	 * and direct hosts therefore share the same owner contract even though disposal
	 * remains specific to TTViewStack or this transient mount.
	 * Like TTVueUtils.mountComponent, this is synchronous and refuses an occupied mount ID;
	 * callers explicitly unmount before replacing a flow (including MFA handoffs).
	 */
	static openShell( mount_id, definition, owner_component, owner_props = {} ) {
		if ( !( definition instanceof TTSShell ) || !definition.isValidDefinition() || definition.getType() !== 'custom' || !definition.getName() || owner_component?.name !== definition.getComponent() || typeof owner_component?.methods?.close !== 'function' ) {
			Debug.Error( 'Invalid custom TTSShell definition or owner.', 'TTRouter.js', 'TTRouter', 'openShell', 1 );
			return false;
		}

		// Imported JS is already trusted executable code. The backend string registry
		// remains the route-loader boundary; no arbitrary import path is evaluated here.
		return TTVueUtils.mountComponent( mount_id, TTWindowManager, {
			direct_shell: markRaw( new TTSShell( definition ) ),
			direct_component: markRaw( owner_component ),
			direct_props: { ...owner_props, component_id: mount_id },
		} );
	}

	/**
	 * Register the authenticated-shell report presenter.
	 *
	 * TTRouter owns the cross-screen launch contract, while the presenter owns
	 * report generation and rendering. Keeping the URL untouched is what lets a
	 * closed report reveal the exact view that was already mounted underneath it.
	 */
	static registerReportModal( handler ) {
		this.report_modal_handler = handler;
	}

	static unregisterReportModal( handler ) {
		if ( this.report_modal_handler === handler ) {
			this.report_modal_handler = null;
		}
	}

	/**
	 * Open report HTML over the current authenticated screen without navigating.
	 */
	static openReportModal( options = {} ) {
		const api_class = String( options.api_class || '' ).trim();
		if ( !/^API[A-Za-z0-9_]+Report$/.test( api_class ) ) {
			throw new Error( 'A valid report API class is required.' );
		}

		const source_config = options.config && typeof options.config === 'object' && !Array.isArray( options.config ) ? options.config : {};
		let config;
		if ( typeof structuredClone === 'function' ) {
			try {
				config = structuredClone( source_config );
			} catch ( error ) {
				// TTS dashboard values may still be Vue proxies. Their JSON shape is
				// the intended API contract even though structuredClone rejects them.
			}
		}
		if ( !config ) {
			config = JSON.parse( JSON.stringify( source_config ) );
		}

		if ( !this.report_modal_handler?.open ) {
			Debug.Text( 'Skipped opening report because the authenticated report presenter was unavailable.', 'TTRouter.js', 'TTRouter', 'openReportModal', 10 );
			return false;
		}

		this.report_modal_handler.open( {
			api_class,
			config,
			title: String( options.title || '' ).trim(),
			ignore_warning: options.ignore_warning === true,
		} );
		return true;
	}

	/**
	 * Open trusted, already-generated report HTML in the shared report shell.
	 *
	 * Specialized workflow APIs such as the Tax Wizard do not expose the ordinary
	 * API*Report.getReport contract. Keeping their presentation on this router-owned
	 * boundary prevents each workflow from recreating report modal chrome/lifecycle.
	 */
	static openRenderedReportModal( options = {} ) {
		const report_html = typeof options.report_html === 'string' ? options.report_html : '';
		if ( report_html === '' ) {
			throw new Error( 'Generated report HTML is required.' );
		}

		if ( !this.report_modal_handler?.open ) {
			Debug.Text( 'Skipped opening rendered report because the authenticated report presenter was unavailable.', 'TTRouter.js', 'TTRouter', 'openRenderedReportModal', 10 );
			return false;
		}

		this.report_modal_handler.open( {
			report_html,
			title: String( options.title || '' ).trim(),
			reload_report: typeof options.reload_report === 'function' ? options.reload_report : null,
		} );
		return true;
	}

	static closeReportModal() {
		if ( !this.report_modal_handler?.close ) {
			return false;
		}

		this.report_modal_handler.close();
		return true;
	}

	/**
	 * Open an ordinary New/Edit/View record (including Mass Edit) using the user's
	 * presentation preference. Callers retain permission checks and route payloads;
	 * Jump To, utility dialogs, and general navigation must keep using navigate().
	 *
	 * Eligibility uses the actual launch source, never the active window or a primary
	 * ancestor. Supply source_view_id when a route's return parent differs from its
	 * launcher, and is_sub_view for embedded lists that share their owner's stack ID.
	 * Reproduction this boundary protects: TimeSheet -> edit punch -> Dock Right ->
	 * enable Open Records in Right Pane -> edit another punch. Checking the setting
	 * only in ListRecordsTable made Employees work while custom boards ignored it.
	 * New and Mass Edit had the same gap because they bypassed the single-row handler.
	 * Keep preference, occupied-slot discard and fallback together for every caller.
	 *
	 * Input ownership:
	 * - route_data already contains the authorized action, IDs, mass flag, defaults,
	 *   factory payload and return context. Presentation must not reconstruct it or
	 *   turn a View request into Edit (Request Authorization depends on that split).
	 * - source_view_id is the launching stack item; explicit null means unavailable.
	 *   Omission permits the route's parent as a default, not the current active view.
	 * - is_sub_view is required when an embedded table shares its owner's stack ID;
	 *   a top-level-looking ID alone cannot prove that the command came from that list.
	 * - path, when supplied, is the matching module-owned URL for this same route.
	 *   Accumulated Time needs its employee query parameter as well as the live parent.
	 *
	 * Return ownership: actual navigation returns the underlying router result or
	 * right-pane promise; this is not an async method with a completion guarantee.
	 * A pending/rejected discard returns true without navigating; it must not trigger
	 * a modal fallback or leave the context-action overlay blocking the confirmation.
	 */
	static navigateRecord( route_data, options = {} ) {
		const view_stack = this.getViewStackStore();
		// The active window may be the existing right-pane editor even though the user
		// clicked its source board. Conversely, New inside an editor may deliberately
		// return to the outer list. Neither active state nor a normalized return parent
		// may replace the caller's actual source. Preserve explicit null to fail closed.
		const source_view_id = options.source_view_id !== undefined ? options.source_view_id : this.getParentViewIdFromTTRoute( route_data );
		// Issue #3924: Ordinary modal records need the same serializable return identity as panes.
		// Capture it on the pending request, not on the mounted source. Preserve an authored fallback
		// (including explicit null): the actual placement source can differ from the intended return parent.
		// This must precede the modal/sub-view eligibility exit below: otherwise disabling
		// Auto Pin loses Dashboard's return identity while opening the same record in a pane
		// happens to work. Stamp only an absent property; truthiness/null-coalescing would
		// overwrite an intentional null or an ID-only return owner supplied by the caller.
		// setData merges the existing payload so editor defaults and navigation snapshots
		// survive; it must never mutate the source window's own route or store Vue refs.
		if ( source_view_id != null && ['new', 'edit', 'view'].includes( route_data?.getAction?.() ) && !Object.prototype.hasOwnProperty.call( route_data.getData()?.view_stack || {}, 'source_view_id' ) ) {
			route_data.setData( { ...route_data.getData(), view_stack: { ...route_data.getData()?.view_stack, source_view_id } } );
		}
		// Mass Edit is still action=edit plus a mode flag. The store checks live source
		// visibility/presentation and the preference; the caller supplies embedded-list
		// status. Run all of these checks before prepareRightPane can climb ancestors.
		if ( options.is_sub_view === true || !['new', 'edit', 'view'].includes( route_data?.getAction?.() ) || !view_stack?.canAutoPinListRecordOpen( source_view_id ) ) {
			return options.path ? this.navigatePath( options.path, { tt_route_data: route_data } ) : this.navigate( route_data );
		}

		// An occupied slot is a replacement candidate, not a reason to fall back to a
		// modal. This includes panes docked manually before the preference was enabled.
		const existing_right_pane = view_stack.findRightPaneView( source_view_id );
		if ( existing_right_pane ) {
			// Reusing the detail slot remounts its editor. Preserve the established
			// discard contract before any placement mutation, including New/Mass Edit.
			// A clean view invokes acceptance synchronously; a dirty one invokes it later
			// only on Yes. Capture the former result without inventing a promise waiting
			// for a callback that No, alert dismissal or alert replacement may never call.
			let confirmed_navigation_result;
			if ( view_stack.requestViewDiscard( existing_right_pane.id, () => {
				// No placement or route mutation is allowed before this accepted callback.
				// A strict false means placement failed; a router promise is not failure.
				// Keep the custom URL bound to this exact route object in either branch.
				const right_pane_result = this.openInRightPane( route_data, { source_view_id, path: options.path } );
				confirmed_navigation_result = right_pane_result === false
					? ( options.path ? this.navigatePath( options.path, { tt_route_data: route_data } ) : this.navigate( route_data ) )
					: right_pane_result;
			} ) !== true ) {
				// Consume the command while the confirmation owns interaction. Returning
				// false here would let callers retry as an ordinary modal and bypass No;
				// retaining the context-action cover would make the alert unclickable.
				ProgressBar.closeOverlay();
				return true;
			}
			return confirmed_navigation_result;
		}

		// Placement may fail if the source disappeared; preserve the user command
		// through ordinary navigation only for that failure, never a declined discard.
		const right_pane_result = this.openInRightPane( route_data, { source_view_id, path: options.path } );
		return right_pane_result === false
			? ( options.path ? this.navigatePath( options.path, { tt_route_data: route_data } ) : this.navigate( route_data ) )
			: right_pane_result;
	}

	/**
	 * Open any routed Vue view beside its owning primary view.
	 *
	 * Callers supply ordinary TTRouteData and may select `source_view_id`, `slot`,
	 * `width`, `path`, or `reuse: false`; source defaults to the route parent and then the
	 * active primary, slot defaults to `detail`, width defaults to 35%, and reuse is
	 * enabled. TTViewStack owns the resulting geometry, visibility, and persistence.
	 * The returned promise resolves after Vue Router assigns the target stack id,
	 * while invalid requests fail synchronously with `false`.
	 *
	 * This API also supports deliberately placed companions/tools, so it does not
	 * check the record-opening preference or ask to discard a reused editor. Its
	 * caller owns those policies. navigateRecord performs both before entering here;
	 * moving them into this primitive would change explicit nested/tool placement.
	 */
	static openInRightPane( route_data, options = {} ) {
		const TTViewStackStore = this.getViewStackStore();

		if ( !TTViewStackStore ) {
			Debug.Text( 'Skipped opening right pane because the view stack store was unavailable.', 'TTRouter.js', 'TTRouter', 'openInRightPane', 10 );
			return false;
		}

		if ( !route_data || typeof route_data.getModule !== 'function' || typeof route_data.getData !== 'function' || typeof route_data.setData !== 'function' || !route_data.getModule() ) {
			Debug.Text( 'Skipped opening right pane because route data was incomplete.', 'TTRouter.js', 'TTRouter', 'openInRightPane', 10 );
			return false;
		}

		// Unlike preference eligibility, explicit placement can resolve a primary
		// ancestor. prepareRightPane also updates grid geometry/source visibility;
		// therefore automatic callers must have completed their discard check first.
		const placement = TTViewStackStore.prepareRightPane( options.source_view_id ?? this.getParentViewIdFromTTRoute( route_data, TTViewStackStore.active_view_id ), {
			slot: options.slot,
			reuse: options.reuse,
			width: options.width,
		} );

		if ( !placement ) {
			return false;
		}

		// Source identity owns placement and return behavior as one contract. Replace
		// a stale/mismatched caller parent so Cancel/Save cannot target a different
		// view; persisted `source_view_id` remains the restore-time fallback.
		if ( placement.source_view.tt_route_data?.getViewReference?.() && typeof route_data.setParentView === 'function' ) {
			route_data.setParentView( placement.source_view.tt_route_data.getViewReference() );
		}

		// Preserve module-owned defaults, snapshots and Save/Cancel payloads. Merge
		// placement last so ordinary defaults such as Schedule's centered_overlay and
		// is_modal_view=true cannot override the user's selected right-pane geometry.
		// Rebuilding the payload here would lose custom editor context and return data.

		route_data.setData( {
			...( route_data.getData() || {} ),
			view_stack: {
				...( route_data.getData()?.view_stack && typeof route_data.getData().view_stack === 'object' && Array.isArray( route_data.getData().view_stack ) !== true ? route_data.getData().view_stack : {} ),
				...placement.view_stack,
			},
		} );
		Debug.Text(
			`Opening route in right pane. module=${route_data.getModule()} action=${route_data.getAction?.() ?? 'none'} source_view=${placement.source_view.id} slot=${placement.view_stack.slot} reuse_view=${placement.existing_view?.id ?? 'none'}`,
			'TTRouter.js',
			'TTRouter',
			'openInRightPane',
			10
		);

		// A module-owned path (for example accumulated-time employee context) must
		// travel with the same route payload through both modal and right-pane opens.
		// Presentation metadata is session-owned rather than URL-encoded. Force the
		// router guard even when module/action/id match the current hash so alternate
		// slots and explicit non-reuse requests still mount their intended pane.
		return Promise.resolve( options.path
			? this.navigatePath( options.path, { tt_route_data: route_data, force_navigation: true } )
			: this.navigate( route_data, { force_navigation: true } ) ).then( () => ( {
			view_id: route_data.getViewStackId?.() ?? placement.existing_view?.id ?? null,
			source_view_id: placement.source_view.id,
			reused: !!placement.existing_view,
		} ) );
	}

	/**
	 * Compatibility adapter for record-oriented callers while new features build
	 * ordinary TTRouteData and call openInRightPane() directly.
	 */
	static openRecordInRightPane( args = {} ) {
		if ( !args?.module ) {
			Debug.Text( 'Skipped opening right pane because no module was supplied.', 'TTRouter.js', 'TTRouter', 'openRecordInRightPane', 10 );
			return false;
		}

		const route_data = new TTRouteData()
			.setModule( args.module )
			.setAction( args.action_id ?? 'view' );
		const record_ids = Array.isArray( args?.record_ids ) ? args.record_ids : ( args?.record_ids ? [args.record_ids] : [] );

		if ( record_ids.length > 0 ) {
			route_data.setRecordIds( record_ids );
		}

		if ( args?.parent_view ) {
			route_data.setParentView( args.parent_view );
		}

		route_data.setData( {
			...( args?.table_data ? { table_data: args.table_data } : {} ),
			...( args?.column_data ? { column_data: args.column_data } : {} ),
		} );

		// Preserve the historical synchronous boolean contract used by context-menu
		// pre-hooks; callers that need the target id can use openInRightPane() directly.
		return this.openInRightPane( route_data, {
			source_view_id: args?.source_view_id,
			slot: args?.slot,
			reuse: args?.reuse,
			width: args?.width,
		} ) !== false;
	}

	/**
	 * Return all stack views when available.
	 */
	static getAllViews() {
		return this.getViewStackStore()?.getAllViews?.() ?? [];
	}

	/**
	 * Merge route parameters into the owning route path.
	 *
	 * Active view updates normally push/replace browser history. Inactive view
	 * updates only patch that stack entry. This protects the active route from
	 * stale async completions in still-mounted special views.
	 */
	static updateURL( params, options = {} ) {
		const TTViewStackStore = this.getViewStackStore();
		const target_view_id = this.getUrlUpdateTargetViewId( options );
		const target_view = this.getUrlUpdateTargetView( TTViewStackStore, options );
		const target_is_active = !target_view || target_view.id === TTViewStackStore?.active_view_id;
		const url = new URL( window.location );
		let hash = target_view?.path || url.hash;

		if ( target_view_id && !target_view ) {
			Debug.Text( 'Skipped URL update for missing stack view id=' + target_view_id, 'TTRouter.js', 'TTRouter', 'updateURL', 10 );
			return '';
		}

		// Remove our unique identifier before URL params to simplify URL parsing.
		if ( hash.startsWith( '#/' ) ) {
			hash = hash.slice( 2 );
		} else if ( hash.startsWith( '#' ) ) {
			hash = hash.slice( 1 );
		} else if ( hash.startsWith( '/' ) ) {
			hash = hash.slice( 1 );
		}

		const search_params = new URLSearchParams( hash );

		for ( const [key, value] of Object.entries( params ) ) {
			if ( value !== null && value !== '' ) {
				search_params.set( key, value );
			}
		}

		// URL-only replacements bypass Vue Router, so seed the same reactive
		// snapshot that a routed navigation would have produced.
		this.updateDeclaredRouteUrlState( target_view?.tt_route_data ?? options?.tt_route_data, search_params );

		if ( target_view && !target_is_active ) {
			return this.updateInactiveStackViewURL( target_view, search_params.toString() );
		}

		if ( options?.history === 'replace' ) {
			return this.replacePathWithoutNavigation( search_params.toString() );
		}

		return this.navigatePath( search_params.toString(), {
			same_surface_rebuild: options?.rebuild === true,
		} );
	}

	/**
	 * Process Vue Router navigation into TTViewStack state.
	 */
	static async processRoute( to, from ) {
		const TTViewStackStore = this.getViewStackStore();
		if ( !TTViewStackStore ) {
			return true;
		}

		if ( ['login', 'install'].includes( to.name ) || ['/login', '/install'].includes( to.path ) ) {
			// Public routes are outside TTViewStack. During logout the authenticated
			// shell can still be mounted while Vue Router guards run; letting this
			// no-module route fall through would create TTDashboardRoute/DashboardView and
			// start authenticated dashboard API calls after the session was cleared. Install
			// must also bypass TTViewLoader because it owns its fields without TTS.
			if ( TTViewStackStore.getCount() > 0 ) {
				TTViewStackStore.removeAllViews();
			}
			return true;
		}

		// Programmatic pushes carry route data and presentation options in an exact
		// target request. Browser Back/Forward has no live request and reconstructs
		// its route from the URL, preventing one push from consuming another's data.
		const navigation_request = this.getNavigationRequest( to.fullPath );
		const navigation_options = navigation_request?.navigation_options ?? {};
		const pending_navigation_route_data = navigation_request?.tt_route_data ?? null;
		const query_params = this.getUrlParameters( to.fullPath );
		const module = query_params.get( 'm' );
		const action = query_params.get( 'a' ) || 'list';
		const record_id = query_params.get( 'id' ) || null;
		const flags = query_params.get( 'f' ) || null;
		const raw_tab = query_params.get( 'tab' );
		const tab = raw_tab !== null && raw_tab !== '' && Number.isFinite( Number( raw_tab ) )
			? Number( raw_tab )
			: null;
		const active_view = TTViewStackStore.getViewById( TTViewStackStore.active_view_id );
		const active_is_modal = active_view && TTViewStackStore.isModalView( active_view ) === true;
		const {
			source_index,
			source_view,
		} = this.getNormalizedRoutingSource( TTViewStackStore, pending_navigation_route_data?.getData?.()?.view_stack?.source_view_id ?? null );

		if ( module ) {
			this.captureReturnStateWhenLeavingSurface( TTViewStackStore, source_view, module, action );

			const target_key = this.normalizePath( to.fullPath );
			const pending_for_reuse = this.preserveActiveModalAsRouteParent( pending_navigation_route_data, active_view, action );
			const reuse_view_id = pending_for_reuse?.getData?.()?.view_stack?.reuse_view_id ?? null;

			if ( reuse_view_id ) {
				const reuse_index = TTViewStackStore.view_stack.findIndex( v => v.id === reuse_view_id );
				const reuse_view = reuse_index !== -1 ? TTViewStackStore.view_stack[reuse_index] : null;

				if ( reuse_view ) {
					const view_stack_overrides = this.getViewStackOverrides( pending_for_reuse, action, source_view, navigation_options );
					const reuse_target_key = this.normalizePath( reuse_view.path ?? '' );

					// Some reused routes carry new in-memory route data without changing the URL, such as Save & Copy replacing a new/edit shell with a copied m=...&a=new route.
					if ( reuse_target_key === target_key && pending_for_reuse?.getData?.()?.view_stack?.force_reuse_rebuild !== true ) {
						if ( view_stack_overrides.has_is_side_panel ) {
							reuse_view.is_side_panel = view_stack_overrides.is_side_panel;
						}
						if ( view_stack_overrides.has_is_modal_view ) {
							reuse_view.is_modal_view = view_stack_overrides.is_modal_view;
						}
						if ( view_stack_overrides.has_is_side_panel || view_stack_overrides.has_is_modal_view ) {
							TTViewStackStore.setRouteViewStackFlags?.( reuse_view, {
								is_side_panel: reuse_view.is_side_panel === true,
								is_modal_view: reuse_view.is_modal_view === true,
							} );
						}

						this.positionReusedView( TTViewStackStore, reuse_view, reuse_index, source_view, source_index, view_stack_overrides );
						this.showReusedStackView( TTViewStackStore, reuse_view.id, navigation_options );

						return true;
					}

					if ( pending_for_reuse ) {
						pending_for_reuse.setInstanceId( reuse_view.id );
						if ( typeof pending_for_reuse.setViewStackId === 'function' ) {
							pending_for_reuse.setViewStackId( reuse_view.id );
						}
					}

					reuse_view.component = markRaw( this.getTTViewLoaderComponent() );
					reuse_view.tt_route_data = pending_for_reuse;
					reuse_view.path = to.fullPath;
					TTViewStackStore.bumpRouteRevision( reuse_view.id );

					if ( view_stack_overrides.has_is_side_panel ) {
						reuse_view.is_side_panel = view_stack_overrides.is_side_panel;
					}
					if ( view_stack_overrides.has_is_modal_view ) {
						reuse_view.is_modal_view = view_stack_overrides.is_modal_view;
					}
					if ( view_stack_overrides.has_is_side_panel || view_stack_overrides.has_is_modal_view ) {
						TTViewStackStore.setRouteViewStackFlags?.( reuse_view, {
							is_side_panel: reuse_view.is_side_panel === true,
							is_modal_view: reuse_view.is_modal_view === true,
						} );
					}

					this.positionReusedView( TTViewStackStore, reuse_view, reuse_index, source_view, source_index, view_stack_overrides );
					this.showReusedStackView( TTViewStackStore, reuse_view.id, navigation_options );

					return true;
				}
			}

			// Same module/action navigation owns the "do not stack more DOM" rule.
			// Active/source matches cover local selector and tab updates. The list-only
			// stack search covers returning to an already-mounted list from menus or
			// browser history without recreating that list DOM.
			// A new companion may intentionally show the same module/action as its
			// source. Normal same-surface reuse would convert the source itself into the
			// side panel, so only explicit source/slot reuse is valid for this presentation.
			let same_surface_view = pending_for_reuse?.getData?.()?.view_stack?.presentation === 'right_pane'
				? null
				: this.getSameSurfaceStackView( TTViewStackStore, active_view, source_view, module, action );
			// Issue #3719: The route explicitly names the current edit as parent. Reusing
			// that same stack item for a linked record would destroy the parent form.
			// Keep normal same-record edits and unrelated same-surface navigations on
			// their existing reuse path; record identity matters only in this relationship.
			if ( same_surface_view && action === 'edit' && record_id && pending_for_reuse?.getParentView?.()
				&& this.getParentViewIdFromTTRoute( pending_for_reuse ) === same_surface_view.id ) {
				// Compare the mounted route's actual record ID rather than a description
				// shown by the link or a possibly changed pending route payload.
				const mounted_record_id = this.getUrlParameters( same_surface_view.path ).get( 'id' );
				// Issue #3719: A nested edit of another record must keep its parent
				// editor mounted so Cancel can return to its unsaved form state.
				if ( mounted_record_id && mounted_record_id !== record_id ) {
					same_surface_view = null;
				}
			}
			if ( same_surface_view ) {
				const same_surface_index = TTViewStackStore.view_stack.findIndex( v => v.id === same_surface_view.id );
				const returning_from_modal_to_parent_list = active_is_modal
					&& action === 'list'
					&& active_view?.id !== same_surface_view.id
					&& source_view?.id === same_surface_view.id;
				const pending_route_data = this.preserveActiveModalAsRouteParent( pending_navigation_route_data, active_view, action );
				const preserve_same_surface_dom = this.shouldPreserveSameSurfaceDOM(
					same_surface_view,
					to.fullPath,
					action,
					navigation_options,
					returning_from_modal_to_parent_list,
					pending_route_data
				) && this.hasExplicitSameSurfaceRoutePayload( pending_route_data ) !== true;
				const declared_url_state_params = RouteSurfacePolicy.getDeclaredUrlStateParams( same_surface_view.tt_route_data );
				const declared_url_state_changed = preserve_same_surface_dom
					&& declared_url_state_params.length > 0
					&& RouteSurfacePolicy.hasDeclaredUrlStateChanged( same_surface_view.path, to.fullPath, same_surface_view.tt_route_data );
				const tt_route_data = preserve_same_surface_dom && declared_url_state_params.length > 0
					? this.updateRouteDataFromUrlParameters( this.mergeDeclaredSameSurfaceRouteData( same_surface_view.tt_route_data, pending_route_data ), query_params, module, action, record_id, flags, tab, true )
					: pending_route_data
					?? (
						preserve_same_surface_dom
							? this.updateRouteDataFromUrlParameters( same_surface_view.tt_route_data, query_params, module, action, record_id, flags, tab, true )
							: this.createRouteDataFromUrlParameters( query_params, module, action, record_id, flags, tab )
					);

				if ( this.updateSameSurfaceStackView( TTViewStackStore, same_surface_view, tt_route_data, to.fullPath, {
					rebuild: !preserve_same_surface_dom,
				} ) ) {
					const view_stack_overrides = this.getViewStackOverrides( tt_route_data, action, source_view, navigation_options );
					if ( view_stack_overrides.has_is_side_panel ) {
						same_surface_view.is_side_panel = view_stack_overrides.is_side_panel;
					}
					if ( view_stack_overrides.has_is_modal_view ) {
						same_surface_view.is_modal_view = view_stack_overrides.is_modal_view;
					}
					if ( view_stack_overrides.has_is_side_panel || view_stack_overrides.has_is_modal_view ) {
						TTViewStackStore.setRouteViewStackFlags?.( same_surface_view, {
							is_side_panel: same_surface_view.is_side_panel === true,
							is_modal_view: same_surface_view.is_modal_view === true,
						} );
					}

					if (
						active_is_modal &&
						active_view?.id &&
						active_view.id !== same_surface_view.id &&
						// Repro protected here: restore Payroll Processing Wizard, then
						// choose Requests a second time. The Requests list is reused, but
						// the Wizard must survive until BaseWizardView collapses it after
						// this route commits. Ordinary modal Cancel/return still removes
						// the active modal through the remaining parent-list conditions.
						active_view.id !== navigation_options.preserve_source_view_id &&
						(
							this.getParentViewIdFromTTRoute( active_view.tt_route_data ) === same_surface_view.id ||
							returning_from_modal_to_parent_list
						)
					) {
						TTViewStackStore.removeView( active_view.id, false );
					}

					// Reusing an existing list still needs the same source visibility
					// rules as adding a fresh view. Otherwise the stack can say the
					// reused view is active while the previous primary view remains
					// visible over it.
					this.positionReusedView( TTViewStackStore, same_surface_view, same_surface_index, source_view, source_index, view_stack_overrides );

					this.showReusedStackView( TTViewStackStore, same_surface_view.id, declared_url_state_changed || preserve_same_surface_dom !== true
						? {
							...navigation_options,
							refresh_on_return: false,
						}
						: navigation_options );

					return true;
				}
			}
		}

		let tt_route_data = this.preserveActiveModalAsRouteParent( pending_navigation_route_data, active_view, action );
		const id = TTUUID.generateUUID();
		let component = this.getTTDashboardRouteComponent();

		Debug.breakIfActive( 'routing' );

		if ( module ) {
			component = this.getTTViewLoaderComponent();

			if ( !tt_route_data ) {
				tt_route_data = this.createRouteDataFromUrlParameters( query_params, module, action, record_id, flags, tab );
				if ( !tt_route_data ) {
					return true;
				}
			}
			tt_route_data = this.preserveActiveModalAsRouteParent( tt_route_data, active_view, action );

			tt_route_data.setInstanceId( id );
		}

		const view_stack_overrides = this.getViewStackOverrides( tt_route_data, action, source_view, navigation_options );
		if ( source_view && !view_stack_overrides.keep_source_visible ) {
			this.hideOrCollapseSourceView( TTViewStackStore, source_view, view_stack_overrides );
		}

		TTViewStackStore.addView(
			{
				// Using markRaw prevents Vue from making the component reactive and re-rendering every stacked view.
				component: markRaw( component ),
				// The default dashboard has no TTRouteData/loader. Declare its label
				// here, where that route ownership is known, before lazy mounting.
				// The shared title composer can then describe the default route without
				// hard-coding Dashboard or pretending that it is a generic list screen.
				view_label: !module ? TTi18n.getText( 'Dashboard' ) : null,
				tt_route_data: tt_route_data,
				path: to.fullPath,
				visible: true,
				window_row: view_stack_overrides.window_row,
				window_column: view_stack_overrides.window_column,
				window_row_span: view_stack_overrides.window_row_span,
				window_col_span: view_stack_overrides.window_col_span,
				is_side_panel: view_stack_overrides.is_side_panel,
				is_modal_view: view_stack_overrides.is_modal_view,
				// Issue #3851: Dashboard has no route/view reference. Preserve its stack identity so
				// floating Wizards and their companions share an ancestor; explicit nested parents win.
				// TTViewStack consumes this existing durable parent field for ancestry and route restoration. Without
				// the Dashboard fallback, its Wizard and Assistant have no shared root: the companion can stay hidden
				// and the modal backdrop can span the entire page instead of the left slot. Native window geometry
				// handles their layout once ancestry is intact; opening the Assistant must not popWindowIn the Wizard.
				// An explicit route parent wins over the fallback. For a nested modal, retain the immediate active
				// modal as parent even though placement above was normalized to its primary ancestor. Otherwise
				// closing a nested edit could return to the background page and lose the parent Wizard workflow.
				// Ordinary non-modal navigation does not acquire this fallback relationship.
				persisted_parent_view_id: view_stack_overrides.is_modal_view
					? this.getParentViewIdFromTTRoute( tt_route_data, active_is_modal ? active_view.id : source_view?.id )
					: null,
			},
			id,
			source_index >= 0 ? source_index : null
		);

		return true;
	}

	/**
	 * Process successful route completion into history and stack cleanup state.
	 */
	static afterRoute( to, from, failure ) {
		const navigation_request = this.getNavigationRequest( to.fullPath );
		const navigation_options = navigation_request?.navigation_options ?? {};
		// processRoute has already consumed the payload synchronously. Delete it here
		// before any completion work can start another navigation to the same surface.
		this.deleteNavigationRequest( to.fullPath );

		if ( failure ) {
			return;
		}

		const TTViewStackStore = this.getViewStackStore();
		const source_view = TTViewStackStore?.getViewById?.( navigation_options.pin_record_source_view_id );
		const destination_view = TTViewStackStore?.getViewById?.( TTViewStackStore.active_view_id );
		const destination_params = this.getUrlParameters( to.fullPath );

		if (
			source_view
			&& destination_view
			&& source_view.id !== destination_view.id
			&& destination_view.tt_route_data?.getModule?.() === destination_params.get( 'm' )
			&& destination_view.tt_route_data?.getAction?.() === 'list'
		) {
			// Pin before the stack-size cleanup below can trim the temporarily hidden
			// record. A source-record-specific slot hides any occupied companion but
			// keeps that mounted view (and its unsaved edits) available in the stack.
			TTViewStackStore.pinViewRight( source_view.id, destination_view.id, {
				slot: `jump_to_record:${source_view.id}`,
			} );
		}

		const global_store = this.getGlobalStore();
		if ( global_store ) {
			global_store.incrementHistorySteps();
			global_store.addToHistoryStack( from );
		}

		/*
		Debug.Text( 'History length ' + global_store.history_steps, 'TTRouter.js', 'TTRouter', 'afterRoute', 10 );
		Debug.Arr( from, 'Route From', 'TTRouter.js', 'TTRouter', 'afterRoute', 10 );
		Debug.Arr( to, 'Route To', 'TTRouter.js', 'TTRouter', 'afterRoute', 10 );
		*/

		if ( to.fullPath !== from.fullPath ) {
			// afterRoute receives Vue Router's failure argument above and returns before
			// this point on aborted navigation. Recording here therefore describes a
			// committed result, not merely an attempted menu click.
			const active_view = TTViewStackStore?.getViewById?.( TTViewStackStore.active_view_id );
			// Do not retain full URLs: filter/search route parameters may contain
			// customer data. The mounted route identity is sufficient to explain which
			// application surface successfully handled the navigation.
			TTInteractionManagerObj.recordSemanticInteraction( 'route_changed', {
				// Read identity from the authoritative mounted stack after processRoute
				// has completed. Re-parsing `to` would miss surface reuse/remount choices.
				source: 'TTRouter',
				view_id: active_view?.id,
				module: active_view?.tt_route_data?.getModule?.(),
				route_action: active_view?.tt_route_data?.getAction?.(),
			} );
		}
		if ( TTViewStackStore && from.meta.stack && to.meta.stack ) {
			while ( TTViewStackStore.getCount() > 10 ) {
				if ( TTViewStackStore.removeOldestView( {
					exclude_view_ids: source_view?.id ? [source_view.id] : [],
				} ) !== true ) {
					break;
				}
			}
		}
	}
}

export const VueRouter = TTRouter.createVueRouter();
export { TTRouter };
export default TTRouter;
